import {AfterViewChecked, AfterViewInit, Component, ElementRef, HostListener, OnInit, ViewEncapsulation} from '@angular/core';
import {FaqEntry, QuestionEntry, SubtitleEntryImpl} from '../../models/faqEntry';
import {Router} from '@angular/router';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';

@Component({
  selector: 'app-faq',
  templateUrl: './faq.component.html',
  styleUrls: ['./faq.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom
})
export class FaqComponent implements AfterViewInit {

  questionSets: QuestionEntry[][] = [
    [
      {
        title: '¿Qué es la movilidad eléctrica?',
        subtitles: [
          new SubtitleEntryImpl(
            'Movilidad eléctrica',
            `
<p>
La movilidad eléctrica se refiere a una forma de transportar personas o bienes a través de un vehículo alimentado con
electricidad, sin motor de combustión. El vehículo puede ser un auto de pasajeros, autobuses, trenes y camiones y cuentan
con un medio para almacenar energía a bordo.
</p>
<p>
Se consideran dos tecnologías principales de vehículos eléctricos: vehículos eléctricos de batería (BEV) y vehículos eléctricos
con celda de combustible (FCEV). Por un lado, los BEV funcionan completamente con electricidad y eliminan la necesidad de
combustibles fósiles. Por otro lado, los FCEV funcionan con hidrógeno que la pila de combustible convierte en electricidad.
Las emisiones pueden ser potencialmente cero carbono dependiendo de la fuente de energía utilizada para obtener el
hidrógeno o la energía eléctrica.
</p>`
          ),
          new SubtitleEntryImpl(
            'Importancia de la movilidad eléctrica',
            `
<p>
La importancia de la electrificación del transporte de carga radica, principalmente, en el crecimiento del sector y los impactos
negativos que este crecimiento pueda tener sobre el medioambiente. A nivel mundial, el sector de transporte representa
cerca del 64% del consumo mundial de petróleo y el 23% de las emisiones de CO2. Estas cifras, al igual que otros
contaminantes atmosféricos, se pueden reducir combinando vehículos eléctricos y matriz de generación de electricidad baja
en carbono.
</p>
<p>
En América Latina los sectores de servicio mediano y pesado representan cerca del 30% de las emisiones de CO2 y sólo
representan el 5% de los vehículos que circulan. En Uruguay, según la publicación del Balance Energético Nacional (BEN), el
sector transporte representa el 62% de las emisiones de los sectores generadores de emisiones CO2 por consumo de
combustible. Tanto en Uruguay como en Latinoamérica, el transporte de carga por calle es un sector que está creciendo
aceleradamente y es el responsable de una fracción mayoritaria del consumo de petróleo mundial y por lo tanto también el
responsable de una fracción significativa de las emisiones de CO2.
</p>
<p>
Tanto en Uruguay como en Latinoamérica, el transporte de carga por calle es un sector que está creciendo aceleradamente y
es el responsable de una fracción mayoritaria del consumo de petróleo mundial y por lo tanto también el responsable de una
fracción significativa de las emisiones de CO2.
</p>
<p>
Es debido a esta situación y a la capacidad de los vehículos eléctricos de reducir las emisiones que la movilidad eléctrica es
de gran relevancia hoy en día.
</p>`
          ),
          new SubtitleEntryImpl(
            'Tendencias y estado actual de la movilidad eléctrica en el mundo',
            `
<p>
Recientemente, el atractivo de los vehículos eléctricos, tanto para los gobiernos como para los usuarios, hizo que el mercado
de vehículos eléctricos creciera en todo el mundo. Los ómnibus fueron el primer y más exitoso caso de electrificación en el
mercado de vehículos pesados, pero la creciente demanda de camiones eléctricos está empujando a los fabricantes a ampliar
las líneas de productos.
</p>
<p>
En 2020, las matriculaciones de autos eléctricos aumentaron un 41%, mientras que las ventas mundiales de automóviles
cayeron un 16%, siendo Europa y China los mercados más grandes. Cuando se trata de vehículos pesados (HDV), la
matriculación ha aumentado un 10% en 2020, alcanzando un stock global de 31.000 vehículos donde China domina el
mercado sobre Europa y Estados Unidos. Aunque muchos estudios observan que la electrificación es menos factible en el
transporte de carga pesado debido a limitaciones técnicas, los fabricantes de camiones como Daimler, MAN, Renault, Scania
y Volvo creen que el futuro del transporte será eléctrico.
</p>
<p>
América Latina tiene potencial para aprovechar la tendencia mundial, tanto desde la demanda como desde la oferta de
productos y servicios asociados. Por un lado, cuenta con una alta concentración de áreas urbanas, altas tasas de utilización
de autobuses y uso de camiones para el transporte de carga y, por otro lado, cuenta con la mayor producción de cobre, las
reservas de litio más grandes y, además, cuenta con recursos de energía renovable.
</p>
<p>
En cuanto al estado actual de los vehículos eléctricos a batería en el mundo y en la región, según la Agencia Internacional de
Energía (IEA) y los estándares europeos, la tecnología se encuentra en los niveles 8 y 9 de madurez en zonas líderes como
China y Europa. En la región de América Latina se tienen niveles 5, 6 y 7, lo cual indica que esta región está en proceso de
maduración y mejora continua.
</p>
<p>
Los vehículos eléctricos a celda de combustible se sitúan con un grado de madurez 6 mayoritariamente en Europa y China, y
con un nivel de maduración 3 en América Latina, en base a los avances significativos como el desarrollo de instrumentos de
planeación nacional y el desarrollo de proyectos piloto. China cuenta actualmente con el 94% de buses FCEV y el 99% de
camiones FCEV del mundo.
</p>
<h6 class="blue-title">Niveles de maduración de tecnología</h6>
<p class="subtitle-one-description">Según el Programa de las Naciones Unidas para el Medio Ambiente (PNUMA)</p>
<div class="center-img"><img src="assets/img/pyr-p1-i1.png" alt="bloques niveles de maduración de teconología"></div>
`
          ),
          new SubtitleEntryImpl(
            'Tendencias y estado actual de la movilidad eléctrica en Uruguay',
            `
<p>
Uruguay actualmente incentiva la movilidad eléctrica y la incorporación de energéticos alternativos con el objetivo de lograr
la soberanía energética, disminuir las emisiones atmosféricas y la contaminación sonora.
</p>
<p>
Las condiciones del país son favorables para el desarrollo del transporte eléctrico en cuanto a generación e infraestructura,
y es por esta razón que se han desarrollado políticas que promueven la comercialización de esta tecnología. En cuanto a la
generación de energía eléctrica, el 98% de la generación es a partir de fuentes renovables, destacando la eólica, hidro, solar
y biomasa. Del total de la energía demandada, el 27% se utiliza para el sector de transporte.
</p>
<p>
Uruguay fue parte de los países que firmaron el Acuerdo de París y que luego lo ratificaron para afirmar su compromiso con
el medio ambiente. En base a esto, el 3 de noviembre de 2017 en Uruguay se aprueba por decreto del poder ejecutivo número
310 la Primera Contribución Determinada (CND) a dicho acuerdo. La tabla debajo muestra, en lo relativo al transporte, una
comparativa entre la situación actual y las principales medidas de mitigación en el sector transporte establecidas para
cumplir los objetivos incondicionales de mitigación de la CDN.
</p>
<!--<div class="img-container">-->
<p class="subtitle-two-description">Comparativa entre objetivos incondicionales de mitigación de la CDN y situación actual</p>

<div class="cnd-table-container">
  <h6 class="cnd-header big-text">Sector Transporte</h6>
  <div class="col-two big-text">CND incondicional</div>
  <div class="col-three big-text">Real</div>
  <div class="col-one big-text">Biocombustibles</div>
  <div class="col-two small-text">5% de mezcla de bioetanol en naftas y 5% de mezcla de biodiesel en gasoil</div>
  <div class="col-three small-text">10% de mezcla de etanol anhidro en naftas y 7% de biodiesel en gasoil</div>
  <div class="col-one big-text">Transporte público</div>
  <div class="col-two small-text">15 ómnibus y 150 taxis eléctricos en 2025</div>
  <div class="col-three small-text">33 ómnibus y 62 taxis eléctricos</div>
  <div class="col-one big-text">Vehículos utilitarios</div>
  <div class="col-two small-text">150 unidades a 2025</div>
  <div class="col-three small-text">120 utilitarios de empresas y 92 de UTE</div>
  <div class="col-one big-text">Ruta eléctrica</div>
  <div class="col-two small-text">Ruta eléctrica que cubra con sistemas de alimentación de vehículos eléctricos las rutas nacionales que
  unen Colonia-Montevideo-Chuy</div>
  <div class="col-three small-text">Actualmente Uruguay ya cuenta con la ruta eléctrica que une dichos puntos</div>
</div>
<!--  <img src="/assets/img/transport-table.png" alt="transport table data">-->
<!--</div>-->
<p>A continuación, se muestra una línea del tiempo con las iniciativas que han desarrollado promoviendo la movilidad eléctrica.</p>
<img class="full-width" src="assets/img/timeline.png" alt="timeline">`
          ),
          new SubtitleEntryImpl(
            'Referencias',
            `
<ul class="references-list">
<li>P. de las N. U. para el M. A. PNUMA, “Movilidad eléctrica: Avances en América Latina y el Caribe,” 2020.</li>
<li>“Why Electric trucks? Volvo Trucks.” https://www.volvotrucks.com/en-en/trucks/alternative-fuels/electric-trucks/faq.html (accessed Aug. 05, 2021).</li>
<li>Smart Freight Centre, “Low Emission Fuels and Vehicles for Road Freight,” Amsterdam, 2020.</li>
<li>G. Krishna, “Understanding and identifying barriers to electric vehicle adoption through thematic analysis,” Transp. Res. Interdiscip.
Perspect., vol. 10, p. 100364, 2021, doi: 10.1016/j.trip.2021.100364.</li>
<li>
F. Gonzalez Venegas, M. Petit, and Y. Perez, “Active integration of electric vehicles into distribution grids: Barriers and frameworks
for flexibility services,” Renew. Sustain. Energy Rev., vol. 145, p. 111060, 2021, doi: 10.1016/j.rser.2021.111060.
</li>
<li>
IEA International Energy Agency, “Global EV Outlook 2021 Accelerating ambitions despite the pandemic,” Paris, 2021. Accessed: May 10, 2021.
[Online]. Available: https://www.iea.org/reports/global-ev-outlook-2021.
</li>
<li>“Balance Energético Nacional.” https://ben.miem.gub.uy/caracteristicas3.html (accessed Mar. 20, 2021).</li>
<li>
B. Nykvist and O. Olsson, “The feasibility of heavy battery electric trucks,” Joule, pp. 901–913, 2021, doi: 10.1016/j.joule.2021.03.007.
</li>
<li>
“Movilidad eléctrica | MIEM,” 2020. https://www.miem.gub.uy/energia/movilidad-electrica (accessed May 26, 2021).
</li>
</ul>
`
          )]
      },
      {
        title: '¿Qué tipos de camiones hay?',
        subtitles: [
          new SubtitleEntryImpl(
            'Camiones eléctricos y su viabilidad en Uruguay',
            `
<p>
Un camión eléctrico es un vehículo con uno o más motores eléctricos que se alimenta mediante baterías
(cargadas a través de conexión a la red eléctrica) o directamente de celdas de combustible (hidrógeno).
</p>
<p>
Los vehículos eléctricos de batería son un tipo de vehículo que no está equipado con un motor de combustión interna y sólo usa energía
proveniente de baterías cargadas de una fuente externa de electricidad.
</p>
<p>
Los vehículos tipo FCEV (vehículos eléctricos de pila de combustible) son vehículos basados en la tecnología de propulsión por hidrógeno.
Utilizan la tecnología de las pilas de combustible, lo que significa que durante la reacción del hidrógeno con el oxígeno se produce la
energía eléctrica que alimenta el motor. El resultado de este proceso es el agua, que se expulsa al exterior del coche mediante un tubo de escape.
</p>
<p>
Los camiones a batería son 100% viables en Uruguay, aunque la aplicación de estos dependerá de las necesidades de cada empresa y de la
oferta de camiones (ver <span class="black-underline" data-truck-models-anchor-tag>Oferta de camiones en Uruguay</span>). Por otro lado, los camiones con pila de combustible de hidrógeno aún se encuentran
en etapa de investigación y desarrollo. El proyecto piloto H2U, liderado por el Ministerio de Industria, Energía y Minería (MIEM) y con
participación de UTE y ANCAP, prevé una flota de camiones de hidrógeno que funcione por mínimo diez años para testear la tecnología.
Sin embargo, aún hay un camino largo por recorrer y el sector privado tendrá un rol clave dado que la inversión, el montaje de la planta,
la provisión de la flota vehicular, el mantenimiento y la ejecución será a cargo de privados.
(<a class="black-underline" href="https://www.gub.uy/ministerio-industria-energia-mineria/comunicacion/noticias/uruguay-lanza-proyecto-piloto-h2u-para-avanzar-hacia-produccion-hidrogeno" target="_blank">Novedades sobre el proyecto aquí</a>)
</p>
`
          ),
          new SubtitleEntryImpl(
            'Electrificación de sectores',
            `
<p>
Haciendo foco en los camiones eléctricos a batería, dado que son los únicos viables hasta el momento en Uruguay,
existe una problemática técnica respecto a electrificar todos los sectores del transporte de carga.
</p>
<p>
El rango o la autonomía del camión depende de la capacidad de la batería, que a su vez también afecta al tamaño de la batería. Aumentar el
tamaño de la batería para extender el rango máximo está limitado por límites legales y económicos sobre el peso del camión. Además, el
tiempo de carga aumentará y la capacidad de carga útil se reducirá con baterías más grandes. La capacidad útil es la cantidad de energía
que el vehículo puede utilizar para propulsarse y es menor a la capacidad total dado que los fabricantes establecen márgenes para evitar
que las baterías se agoten completamente hasta su último electrón porque puede dañarla o acortar su vida útil.
</p>
<p>
La capacidad de energía se mide mediante la capacidad de una batería (kWh) de entregar una potencia de salida establecida (en kilovatios)
durante un período de tiempo (en horas). Las consideraciones de rendimiento para las baterías se basan en las densidades de energía
volumétricas, la potencia específica, la durabilidad y el número de ciclos de descarga que puede soportar una batería antes de perder una
importante fracción de la capacidad. La mayoría de las baterías para camiones eléctricos utilizan una de las combinaciones químicas de
ion-litio disponibles comercialmente. Estas y otras combinaciones están actualmente en el centro de muchos esfuerzos de investigación para
mejorar su rendimiento.
</p>
<p>
Hoy en día en Uruguay el transporte urbano de servicio mediano de carga es viable y ya hay aplicaciones en el país que lo demuestran.
Cuando se trata de servicios pesados (mayor a 15 toneladas), los camiones eléctricos podrían transportar aproximadamente un 40% menos de
productos que los camiones a combustión interna considerando el mismo rango de manejo debido al peso de la batería, lo que supone pérdidas
económicas importantes.
</p>
`
          ),
          new SubtitleEntryImpl(
            'Referencias',
            `
<ul class="references-list">
<li>
B. Ślusarczyk, “Electromobility for sustainable transport in Poland,” Energy Transform. Towar. Sustain., pp. 199–218,
2019, doi: 10.1016/B978-0-12-817688-7.00010-0.
</li>
<li>
IEA International Energy Agency, “Global EV Outlook 2021 Accelerating ambitions despite the pandemic,” Paris, 2021.
Accessed: May 10, 2021. [Online]. Available: https://www.iea.org/reports/global-ev-outlook-2021.
</li>
<li>Proyecto hidrógeno verde: https://www.gub.uy/ministerio-industria-energia-mineria/tematica/hidrogeno-verde</li>
<li>
Shell and Deloitte, “Decarbonising Road Freight: Getting into gear,” 2021. Accessed: Apr. 29, 2021. [Online].
Available: www.shell.com/DecarbonisingRoadFreight.
</li>
</ul>
`
          )]
      },
      {
        title: '¿Por qué usaría un camión eléctrico?',
        subtitles: [
          new SubtitleEntryImpl(
            'Beneficios',
            `
<p>Algunas de las razones por las cuales se debería considerar un camión eléctrico:</p>
<ul class="e-truck-question-one-list">
<h6 class="row-one">Disminuye el consumo de combustibles fósiles</h6>
<li>
al cambiar a camiones eléctricos a batería o a celda de combustible de hidrógeno se reduce el consumo de combustibles fósiles porque el
98% de la energía utilizada para cargar el camión proviene de fuentes renovables en Uruguay y, por otro lado, el hidrógeno que se planea
obtener en el proyecto H2U es hidrógeno verde (que sea de tipo verde indica que proviene de una fuente de energía renovable).
</li>

<h6 class="row-one">Disminuye la contaminación de gases de efecto invernadero (GEI)</h6>
<li>
En la actualidad, los BEV brindan reducciones de emisiones de GEI durante el ciclo de vida de alrededor del 20-30% en relación con los
vehículos de combustión interna convencionales en un promedio mundial y dicha reducción depende de las emisiones durante la generación de
energía.
</li>

<h6 class="row-one">Disminuye la contaminación sonora</h6>
<li>
el ruido de propulsión es considerablemente menor sin un motor de combustión. Esto
tiene mayor impacto cuando los vehículos operan a baja velocidad, como lo hacen normalmente en áreas urbanas, donde la
contaminación acústica es la mayor preocupación. A bajas velocidades, los vehículos eléctricos pueden ser hasta 14 dB más
silenciosos que un vehículo de combustión equivalente, lo que los hace más agradables tanto para el operador como para el
vecindario.
</li>

<h6 class="row-three">Menos gastos en mantenimiento y menos paradas por mantenimiento</h6>
<li>
Aunque los vehículos eléctricos puros requieren algún mantenimiento programado para sus sistemas eléctricos, esto es mínimo en comparación
con el requerido para los camiones de diésel. Esto se debe a que un vehículo eléctrico puro tiene muchas menos partes móviles que un motor
de combustión y no hay aceite o fluidos de transmisión que reemplazar. Los sistemas de frenos también duran más que los vehículos
convencionales, gracias a sus sistemas de frenado regenerativo. Estos sistemas utilizan el motor del vehículo eléctrico como generador para
convertir gran parte de la energía cinética perdida al desacelerar nuevamente en energía almacenada en la batería del vehículo.
</li>

<h6 class="row-three">Capacidad de entregar un par máximo a cero rpm</h6>
<li>
Una característica que es exclusiva de los camiones eléctricos es el par instantáneo o la capacidad de entregar un par máximo a cero
revoluciones por minuto (rpm). Esto permite a los camiones eléctricos remolcar y transportar una gran carga útil a velocidades mucho más
bajas que los camiones convencionales. A cero rpm, un motor eléctrico puede convertir toda la electricidad en torque, sin ninguna fuerza
electromagnética (EMF) que lo frene.
</li>

<h6 class="row-three">Costos totales de propiedad (TCO)</h6>
<li>
Un estudio realizado en Uruguay que evalúa los costos totales de propiedad de camiones eléctricos a batería demuestra que se llegará a la
paridad de costos en comparación a los camiones a diésel en 2026, o a más tardar, en 2027, considerando el escenario más desfavorable. Sin
embargo, la evaluación de los costos totales se ven muy influenciados por los incentivos y beneficios que se obtengan en el momento de la
compra, así como del uso que se le da al camión. Si estás interesado en hacer un análisis comparativo del TCO de camiones eléctricos y
diésel, utiliza nuestra herramienta.
</li>
</ul>
`
          ),
          new SubtitleEntryImpl('Principales diferencias entre camiones eléctricos y camiones a combustión interna', `
<p>
Los vehículos con motor de combustión interna se distinguen por tener un energético a bordo que emite emisiones de escape a la atmósfera
en el momento del uso, tener una gran cantidad de partes móviles y generar contaminación sonora. En esta clasificación se encuentran los
vehículos de encendido positivo (o chispa) que utilizan gasolina, gas licuado de petróleo (GLP) o gas natural en forma comprimida (GNC) o
licuada (GNL), y los vehículos con motor de encendido por compresión que utiliza diésel o biodiésel (100% o una mezcla de los dos tipos de
combustibles).
</p>
<p>
Por otro lado, los vehículos cero-emisiones se caracterizan por no emitir gases de escape de la fuente de energía en el punto de uso, tener
escasas partes móviles y no emitir sonido. Cabe destacar que el término de cero-emisiones contempla la no emisión de otros contaminantes
además de los GEI. Sin embargo, no abarca las emisiones asociadas a la generación y distribución del energético ni a los procesos de
fabricación de los vehículos.
</p>
<p>
A continuación, se muestra una tabla comparativa elaborada por Shell y Deloitte donde se muestran las principales diferencias entre las
tres tecnologías estudiadas.
</p>
<div class="comparison-chart-grid">
<h6 class="comparison-chart-header comparison-first-header">Diésel</h6>
<h6 class="comparison-chart-header comparison-second-header">Eléctrico a batería</h6>
<h6 class="comparison-chart-header comparison-third-header">Eléctrico con celda de combustible</h6>

<div class="comparison-variable">Energético</div>
<div class="comparison-normal-val">Diésel</div>
<div class="comparison-normal-val">Batería</div>
<div class="comparison-normal-val">Hidrógeno</div>

<div class="comparison-variable">Tren motriz</div>
<div class="comparison-normal-val">ICE</div>
<div class="comparison-normal-val">Motor eléctrico</div>
<div class="comparison-normal-val">Motor eléctrico</div>

<div class="comparison-variable">Combustible de uso directo</div>
<div class="comparison-normal-val">N/C</div>
<div class="comparison-normal-val">No</div>
<div class="comparison-normal-val">No</div>

<div class="comparison-variable">Carbón-cero</div>
<div class="comparison-normal-val">No</div>
<div class="comparison-normal-val">Sí</div>
<div class="comparison-normal-val">Sí</div>

<div class="comparison-variable">Autonomía (km)</div>
<div class="comparison-normal-val">&gt;15000</div>
<div class="comparison-chart-multiple-content">
  <div class="comparison-low-val">~200</div>
  <div class="comparison-normal-val">~800 (planificado)</div>
</div>
<div class="comparison-high-val">400-1200</div>

<div class="comparison-variable">Peso del tren motriz (kton)</div>
<div class="comparison-normal-val">~3</div>
<div class="comparison-chart-multiple-content same-size-grid">
  <div class="comparison-low-val">~2,2</div>
  <div class="comparison-normal-val">~4,9</div>
</div>
<div class="comparison-high-val">~3,6</div>

<div class="comparison-variable">Tiempo de recarga (h)</div>
<div class="comparison-normal-val">0,3-0,4</div>
<div class="comparison-chart-multiple-content same-size-grid">
  <div class="comparison-very-low-val">&gt;3</div>
  <div class="comparison-normal-val">&gt;7</div>
</div>
<div class="comparison-high-val">0,3-0,4</div>

<div class="comparison-variable">TCO y costo inicial del camión</div>
<div class="comparison-normal-val"></div>
<div class="comparison-low-val"></div>
<div class="comparison-very-low-val"></div>
</div>

<div class="comparison-chart-legend">
  <div class="comparison-legend-item">
    <div class="color-box high-val"></div>
    <div class="text-container">En/por encima de la paridad</div>
  </div>

  <div class="comparison-legend-item">
    <div class="color-box low-val"></div>
    <div class="text-container">Apenas por debajo de paridad</div>
  </div>

  <div class="comparison-legend-item">
    <div class="color-box very-low-val"></div>
    <div class="text-container">Debajo de paridad</div>
  </div>
</div>
<p>
Cabe destacar que la autonomía de 800 km para un camión eléctrico a batería fue puesta en virtud del Tesla Semi. Los tiempos de recarga
fueron calculados con cargadores rápidos de 150kW. Si se usan cargadores lentos el tiempo puede aumentar hasta en siete veces. El peso del
tren motriz incluye motor, sistema de enfriamiento, transmisión y combustible/baterías. Se suponen baterías de 270 kWh y 1100 kWh para BEV
(correspondientes a las dos columnas que se muestran en la tabla), se supone un paquete de baterías de 250 kWh para FCEV, la densidad de
energía del paquete de baterías de 0,25 kWh/kg y el consumo de energía de 1,15 kWh/km con un 85% de uso de la batería. Finalmente, se asume
que el motor eléctrico y la caja de cambios son 600 kg, y la pila de combustible eléctrica se asume que 470 kg.
</p>
          `),
          new SubtitleEntryImpl('Referencias', `
<ul class="references-list">
<li>
IEA International Energy Agency, “Global EV Outlook 2021 Accelerating ambitions despite the pandemic,” Paris, 2021. Accessed: May 10,
2021. [Online]. Available: https://www.iea.org/reports/global-ev-outlook-2021.
</li>
<li>“El País - Energía,” 2021. https://servicios.elpais.com.uy/suple/especiales/energia21.html (accessed May 27, 2021).</li>
<li>
“Medium and heavy-duty electrification trends | Geotab.” https://www.geotab.com/white-paper/heavy-duty-fleet- electrification-trends/
(accessed Aug. 16, 2021).
</li>
<li>
M. Tanco, L. Cat, and S. Garat, “A break-even analysis for battery electric trucks in Latin America,” J. Clean. Prod., vol. 228,
pp. 1354–1367, 2019, doi: 10.1016/j.jclepro.2019.04.168.
</li>
</ul>
`)
        ]
      },
      {
        title: '¿Cómo funcionan los camiones eléctricos?',
        subtitles: [new SubtitleEntryImpl('Funcionamiento de las tecnologías', `
<section class="battery-truck-section">
<h6>Camión eléctrico a batería</h6>
<p>
Un camión eléctrico es un vehículo con uno o más motores eléctricos que se alimenta mediante baterías (cargadas a través de conexión a
la red eléctrica) o directamente de celdas de combustible (hidrógeno). Los vehículos eléctricos de batería son un tipo de vehículo que no
está equipado con un motor de combustión interna y sólo usa energía proveniente de baterías cargadas de una fuente externa de electricidad.
</p>
<p>
A continuación, se detallan los procesos que ocurren cuando se enciende un camión eléctrico:
</p>
<ol class="e-truck-list">
<li>Cuando el conductor gira parcialmente la llave, los iones y electrones de las baterías de alto voltaje están disponibles para alimentar el camión.</li>
<li>Una vez que el conductor gira la llave por completo (como si "arrancara" el camión), el sistema de alto voltaje envía energía al tren motriz.</li>
<li>
Al presionarlo, el acelerador envía una señal de software al inversor, un interruptor que se abre y cierra rápidamente para convertir
una señal de corriente continua (CC) en corriente alterna (CA).
</li>
<li>
La energía de CA energiza el motor eléctrico y crea torque. La energía de rotación se alimenta al tren motriz eléctrico, lo que hace que
las ruedas giren y el vehículo se acelere.
</li>
<li>
Además del frenado estándar, el camión se puede detener mediante la recuperación del freno, también conocido como frenado regenerativo
en los vehículos eléctricos de pasajeros. Esto permite que las baterías se recarguen. La energía viaja desde los motores eléctricos hasta el
inversor y regresa a las baterías.
</li>
</ol>

<p>
Los camiones a batería, a diferencia de los camiones diésel convencionales, tienen muchas baterías en todo el vehículo y tienen un
sistema de control para optimizar su carga balanceada y su uso. Las baterías que se encuentran en el camión son de dos tipos:
</p>
<p>
Baterías de alto voltaje: estas baterías le dan a un camión eléctrico su "marcha". Suelen estar ubicadas entre los rieles del marco y
alimentan muchas partes del vehículo. Por ejemplo, estas baterías accionan de uno a tres motores eléctricos (según la configuración), así
como un compresor de aire eléctrico, que proporciona presión de aire para los frenos neumáticos y el sistema de suspensión del camión.
</p>
<p>
Baterías de bajo voltaje: las baterías de bajo voltaje, generalmente ubicadas debajo del capó de un camión eléctrico, alimentan componentes
de bajo voltaje, como los controles en el tablero y varios sistemas integrados.
</p>
</section>

<section class="hydrogen-truck-section">
<h6>Camión eléctrico con celda de hidrógeno</h6>

<p class="htruck-section-blue-title">Disposición de las celdas de combustible</p>
<div class="htruck-section-img-container">
  <img src="assets/img/pyr-p4-i1.png" class="htruck-section-img-one" alt="disposición de las celdas de combustible">
  <p>
  En los camiones eléctricos alimentados con hidrógeno el sistema de propulsión es accionado por celdas de combustible a bordo en donde
  la energía se convierte en electricidad a partir del hidrógeno. Adicionalmente, estos vehículos cuentan con una batería para los momentos
  en que se necesita energía adicional y cuando el vehículo necesita recuperar energía eléctrica de la energía de los frenos.
  </p>
</div>

<p>A continuación, se explica el fenómeno que ocurre en la celda de combustible a partir de la cual se obtiene energía para accionar
al motor eléctrico:</p>

<ol class="htruck-section-first-list">
<li>las moléculas de hidrógeno, que se encuentran almacenadas como gas comprimido en los tanques a bordo del camión, primero ingresan al
electrodo de hidrógeno (llamado ánodo) de la celda de combustible.</li>
<li>las moléculas de hidrógeno luego reaccionan con el catalizador que recubre el ánodo, liberando electrones para formar un ion de
hidrógeno cargado positivamente.</li>
<li>los iones cruzan el electrolito y llegan al oxígeno en el segundo electrodo (llamado cátodo).</li>
<li>los electrones que no pueden pasar el electrolito fluyen hacia un circuito eléctrico, generando la energía del sistema de
pila de combustible.</li>
<li>en el cátodo, el catalizador hace que los iones y electrones de hidrógeno se unan con el oxígeno del aire para formar vapor de agua,
que es el único subproducto del proceso. El vapor de agua se descarga a través del tubo de escape junto con el nitrógeno que ingresó a la
pila de combustible en el aire</li>
</ol>

<div class="htruck-section-second-img-container">
  <h6 class="htruck-section-blue-img-title">Funcionamiento de la celda de hidrógeno</h6>
  <img src="assets/img/pyr-p4-i2.png" alt="funcionamiento de la celda de hidrógeno" class="htruck-section-img-two">
</div>

<p class="add-top-margin">La celda de combustible también alimenta un sistema de suministro de aire que se compone de un filtro de aire,
un compresor de aire y humidificadores proporciona oxígeno a la celda y un sistema de gestión del agua y del calor que tiene circuitos
separados de agua y refrigerante usados para eliminar el calor residual y los productos de la reacción (agua). Además, a través de este
sistema se puede transportar el calor a la cabina del vehículo y mejorar la eficiencia. En la siguiente figura se puede ver un esquema de
lo explicado
anteriormente.</p>

<img class="p-four-img-three" src="assets/img/pyr-p4-i3.png" alt="figura del esquema">

<p>Las pilas de combustible suelen clasificarse según el tipo de electrolito utilizado. Los tipos típicos de electrolitos de celdas de
combustible incluyen:</p>

<ul class="htruck-section-second-list">
<li>La membrana de intercambio de protones ("PEM")</li>
<li>La celda de combustible alcalina ("AFC")</li>
<li>La celda de combustible de ácido fosfórico ("PAFC")</li>
<li>Las celdas de combustible de óxido sólido ("SOFC")</li>
<li>El carbonato fundido pila de combustible (“MCFC”)</li>
</ul>

<p>De estos, el PEM es el tipo más comercializado en la actualidad, debido a su baja temperatura de funcionamiento (50-100 ° C), su corto
tiempo de inicio y la facilidad de uso de su oxidante (aire atmosférico).</p>
</section>
        `), new SubtitleEntryImpl('Autonomía', `
<section class="etruck-section-two">
<h6 class="green-title">Camión eléctrico a batería</h6>
<p>A nivel internacional, numerosos fabricantes de camiones han anunciado nuevas líneas de camiones eléctricos. La mayoría de los modelos
se encuentran en la etapa de creación de prototipos, pruebas en carretera o recién comenzaron a comercializarse. Debido a esto, los valores
de la tabla que se muestra abajo se basan en los rendimientos declarados por los fabricantes. La mayoría de los modelos encontrados están
diseñados para aplicaciones de reparto urbano, con un rango de autonomía inferior a 300 km, para favorecer la capacidad de carga útil.</p>
</section>

<div class="p-four-img-four-wrapper">
<img src="assets/img/pyr-p4-i4.png" alt="tabla de modelos camiones eléctricos" class="pyr-p-four-img-four">
</div>

<section class="htruck-section-two">
<h6 class="blue-title">Camión eléctrico con celda de hidrógeno</h6>
<p>Existen algunos proyectos completados de camiones eléctricos de pila de combustible en Europa y Estados
Unidos. Si bien es ciertamente demasiado pronto para obtener valores de rendimiento en un número sustancial
de kilómetros, se dan algunas especificaciones para algunos de ellos, como se enumera en la tabla que se
muestra debajo. En comparación con los modelos eléctricos de batería descritos en la sección anterior, los
camiones FCEV están diseñados con una mayor autonomía, comprendida principalmente entre 200 y 400 km.</p>
</section>

<div class="p-four-img-five-wrapper">
<img src="assets/img/pyr-p4-i5.png" alt="tabla de camiones a celda de hidrógeno" class="pyr-p-four-img-five">
</div>
        `), new SubtitleEntryImpl('Eficiencia de las tecnologías', `
<p class="add-bottom-margin">En cuanto a eficiencia, el estudio realizado por Smart Freight Centre revela una comparación entre ambas tecnologías basada en las
pérdidas energéticas desde la obtención de la energía hasta su uso final. Este estudio se realizó para vehículos de pasajeros, pero se puede
trasladar los valores aproximados a camiones de carga dado que esencialmente la tecnología es la misma. En la figura que se muestra debajo,
se observa en cada fase de producción y utilización, el porcentaje de eficiencia. En el caso del hidrógeno, las múltiples fases y procesos,
y las bajas eficiencias de estos, hacen que la eficiencia global sea sensiblemente más baja, siendo 30% frente al 76% correspondiente a la
tecnología de batería eléctrica.</p>

<div class="p-four-img-six-wrapper"><img src="assets/img/pyr-p4-i6.jpeg" alt="fases de producción y utilización" class="p-four-img-six"></div>
        `), new SubtitleEntryImpl('Impacto medioambiental de los camiones', `
<div class="flex-container-forth-question">
<div class="paragraphs-container">
<p>Para evaluar el impacto medioambiental de los camiones eléctricos se debe tener en cuenta el ciclo de vida total del vehículo, incluidas
las materias primeras, la producción, el uso y su disposición. Un camión eléctrico (cualquiera de las dos tecnologías) produce cero
emisiones en el tubo de escape sin embargo hay emisiones que se liberan durante la producción y durante la conducción (neumáticos, frenos,
desgaste de la carretera…). Sin embargo, aproximadamente el 90% del impacto ambiental del ciclo de vida de un camión diésel proviene del uso
y los camiones que funcionan con electricidad de fuentes renovables tienen un impacto mucho menor.</p>
<p>El ciclo de vida de un camión se puede descomponer en cuatro fases para su análisis.</p>
</div>
<img src="assets/img/pyr-p4-i7.jpg" alt="ciclo de vida de camión" class="p-four-img-seven">
</div>

<h6 class="black-title">Producción</h6>
<p>Se deben tener en cuenta los materiales utilizados, los procesos a los que se someten los materiales y su respectivo uso de combustibles,
emisiones y lugar en donde son producidos, entre otros. Las emisiones de GEI de la fabricación del sistema de pilas de combustible
representan aproximadamente la mitad de las emisiones de GEI del proceso de fabricación y eliminación de FCEV. Por otro lado, la fabricación
de la batería representa las mayores emisiones de GEI del ciclo de vida de BEV dado que requiere extraer y refinar metales de tierras raras
y consume mucha energía debido al alto calor y las condiciones estériles involucradas. En el proceso de producción de baterías, casi el 50%
de las emisiones de GEI provienen del ensamblaje y fabricación de batería, mientras que la mitad restante proviene principalmente del
proceso de extracción, refinación y procesamiento de materias primas.</p>

<h6 class="black-title">Operación</h6>
<p>El factor determinante del impacto medioambiental es el energético utilizado. Para calcular el impacto de dichos combustibles se
diferencian las operaciones involucradas en el Well-to-Tank (desde la obtención del energético hasta el almacenamiento) y aquellas que
forman parte del alcance del Tank-to-Wheel (desde el almacenamiento hasta el uso). A continuación, se muestra un esquema de ambos análisis.
</p>
<p>En cuanto a los BEV, los resultados favorables se muestran cuando la energía es renovable dado que en este caso las pérdidas energéticas
se dan únicamente en las estaciones eléctricas y en el abastecimiento, pero las emisiones de GEI son nulas. Por otro lado, el hidrógeno se
puede generar con diversos métodos que condicionan el nivel de emisiones. El hidrógeno llamado “gris” se obtiene a partir de metano fósil en
un proceso denominado Reformación de Metano con Vapor (SMR, por sus siglas en inglés). En este proceso las emisiones de GEI son altas y son
liberadas al ambiente. En esta metodología, todas las fases hasta el almacenamiento generan emisiones. El hidrógeno “azul” se obtiene de la
misma forma que el gris con la diferencia que se capta el dióxido de carbono generado en el proceso SMR. De esta forma, las emisiones
disminuyen considerablemente. Por último, el hidrógeno verde se genera a partir de fuentes de energía renovables en donde las emisiones son
nulas a lo largo de la trayectoria del combustible.</p>

<h6 class="black-title">Mantenimiento</h6>
<p>Debe tenerse en cuenta principalmente el cambio de neumáticos, dado que otras operaciones tales como el cambio de aceite, de baterías de
arranque y de pastillas de freno representan entre el 0 y 0,3% de las emisiones de esta fase.</p>

<h6 class="black-title">Recuperación o fin de ciclo de vida</h6>
<p>Los impactos ambientales negativos durante el proceso de eliminación provienen principalmente de la contaminación de materiales metálicos
y el consumo de energía de reciclaje. Cabe señalar que los FCEV y BEV tienen la posibilidad de reducir la huella de carbono y disminuir los
impactos negativos en el medio ambiente si los materiales utilizados en la pila de combustible y la batería se reciclan correctamente.
Normalmente, los FCEV tienen menos emisiones de GEI que los BEV durante el proceso de eliminación a pesar de que las tecnologías de
reciclaje involucradas en los sistemas de pilas de combustible aún se encuentran en etapas tempranas.</p>
<p>Hay tres métodos principales para darle fin a la vida útil de una batería en un BEV, que son la reutilización, el reciclaje y la
eliminación. La reutilización, o aplicación de segunda vida, se puede aplicar a la batería de iones de litio que retiene una parte de su
capacidad original después de ser retirada del vehículo. Esta batería se puede usar en aplicaciones de almacenamiento estacionario, pero
eventualmente la batería llegará al final de su vida útil y será necesario reciclarla o desecharla.</p>
<p>Por otro lado, a medida que crece la industria de los vehículos eléctricos, el reciclaje de baterías también puede volverse más factible.
Al reciclar baterías, los materiales para producir una batería nueva podrían resultar en reducciones en las emisiones de GEI de hasta un 50%
en todo el proceso de producción de la batería.</p>
        `), new SubtitleEntryImpl('Referencias', `
<ul class="references-list">
<li>B. Ślusarczyk, “Electromobility for sustainable transport in Poland,” Energy Transform. Towar. Sustain., pp. 199–218, 2019, doi:
10.1016/B978-0-12-817688-7.00010-0.</li>
<li>“Beyond The Batteries: How Electric Trucks Work | Freightliner Trucks.”
https://freightliner.com/blog-and-newsletters/beyondthe-batteries-how-electric-trucks-work/ (accessed Aug. 18, 2021).</li>
<li>“How does a hydrogen fuel cell electric truck work?”
https://www.scania.com/group/en/home/newsroom/news/2020/howdoes-a-hydrogen-fuel-cell-electric-truck-work.html (accessed Aug. 18, 2021).</li>
<li>Deloitte China and Ballard, “Fueling the Future of Mobility Hydrogen and fuel cell solutions for transportation,” Financ. Advis.,
vol. 1, p. Volume 1, 2019.</li>
<li>R. Sacchi, C. Bauer, and B. L. Cox, “Does Size Matter? The Influence of Size, Load Factor, Range Autonomy, and Application Type on the
Life Cycle Assessment of Current and Future Medium? The Heavy-Duty Vehicles,” Environ. Sci. Technol., vol. 55, no. 8, pp. 5224–5235, 2021,
doi: 10.1021/acs.est.0c07773.</li>
<li>Smart Freight Centre, “Low Emission Fuels and Vehicles for Road Freight,” Amsterdam, 2020.</li>
<li>Why Electric trucks? Volvo Trucks.” https://www.volvotrucks.com/en-en/trucks/alternative-fuels/electric-trucks/faq.html
(accessed Aug. 05, 2021).</li>
<li>D. Burul, “Battery electric vs diesel driven,” own Rep. Scania, 2021, [Online]. Available:
https://www.scania.com/group/en/home/newsroom/press-releases/press-release-detail-page.html/3999115-scania-publisheslife-cycle-assessment-of-battery-electric-vehicles.</li>
</ul>
        `)]
      },
      {
        title: '¿Qué tecnología me conviene?',
        subtitles: [new SubtitleEntryImpl('Uso de cada tecnología', `
<p>Es imprescindible analizar las operaciones de la empresa para decidir qué tecnología es conveniente. Hasta el momento, a pesar de los
grandes esfuerzos y avances a nivel país con el hidrógeno, la única tecnología disponible en Uruguay es la eléctrica a batería.</p>
<p>El criterio principal para determinar si el almacenamiento de energía a bordo debe ser celdas de hidrógeno o baterías es que el vehículo
escogido alcance el rango operativo requerido sin la necesidad de recargar en el medio o que esté prevista una parada suficientemente larga.
Las autonomías disponibles para camiones BEV en Uruguay se detallan en la sección de Modelos disponibles.</p>
<p>Los camiones totalmente eléctricos a batería son ideales para transportar mercancías dentro y alrededor de las ciudades en rutas
predefinidas que incluyen un regreso a la base de operaciones al final de la jornada laboral para cargarlas. La distribución, la
construcción urbana y la recolección de basura son ejemplos de segmentos que han sido los primeros en adoptar camiones eléctricos a batería.
Por contraparte, aún no son viables en el transporte de larga distancia, debido a la falta de infraestructura de carga, y las aplicaciones
de transporte pesado en las industrias de la madera, la construcción y la minería. En estas aplicaciones el camión con celdas de hidrógeno
está en ventaja frente al eléctrico dado que la pila y los tanques juntos pesan mucho menos que una batería por lo que la carga útil es
correspondientemente mayor. Además, facilita las operaciones porque tiene un tiempo de llenado de combustible muy parecido al diésel. Por
ejemplo, el camión XCIENT de Hyundai tiene un peso bruto de 36 toneladas puede recorrer 400 km entre recargas, lo que lleva entre 8 y 25
minutos.</p>
        `), new SubtitleEntryImpl('Beneficios y desafíos', `
<p>Principales beneficios:</p>
<div class="flex-container-q-five add-bottom-margin">
<div class="q-five-flex-entry">
<div class="q-five-flex-img-container-one"><img src="assets/img/pyr-p5-i1.png" alt="nube CO2" class="q-five-flex-container-img"></div>
<h6 class="q-five-flex-title">Emisiones</h6>
<p class="q-five-flex-content">Este tipo de vehículos no emiten contaminantes de escape, CO2, ni dióxido de nitrógeno (NO2), aunque la
fabricación de baterías incide negativamente en la huella de carbono, lo cual es un desafío que permite continuar desarrollando y mejorando
los sistemas de producción.</p>
</div>

<div class="q-five-flex-entry">
<div class="q-five-flex-img-container-two"><img src="assets/img/pyr-p5-i2.png" alt="tick" class="q-five-flex-container-img"></div>
<h6 class="q-five-flex-title">Sencillez</h6>
<p class="q-five-flex-content">El número de elementos del motor es menor, lo que conlleva un mantenimiento mucho más económico. Los motores
son más simples y compactos, no necesitan circuito de refrigeración, y tampoco es necesario incorporar cambio de marchas, embrague o
elementos que reduzcan el ruido del motor.</p>
</div>

<div class="q-five-flex-entry">
<div class="q-five-flex-img-container-one"><img src="assets/img/pyr-p5-i3.png" alt="emblema" class="q-five-flex-container-img"></div>
<h6 class="q-five-flex-title">Fiabilidad</h6>
<p class="q-five-flex-content">Tener menos componentes y más sencillos hace que este tipo de vehículos tengan menos averías. Además, los
vehículos eléctricos no sufren el desgaste inherente producido por las explosiones del motor, las vibraciones o la corrosión del
combustible.</p>
</div>
</div>

<div class="flex-container-q-five">
<div class="q-five-flex-entry">
<div class="q-five-flex-img-container-one"><img src="assets/img/pyr-p5-i4.png" alt="símbolo de peso con flecha hacia abajo" class="q-five-flex-container-img"></div>
<h6 class="q-five-flex-title">Costo</h6>
<p class="q-five-flex-content">El costo de mantenimiento del vehículo y el costo de la electricidad requerida es mucho menor en comparación
con los costos de mantenimiento y combustible de los vehículos de combustión tradicionales. El costo de energía por kilómetro es
significativamente menor en los vehículos eléctricos que en los vehículos tradicionales.</p>
</div>

<div class="q-five-flex-entry">
<div class="q-five-flex-img-container-two"><img src="assets/img/pyr-p5-i5.png" alt="eficiencia" class="q-five-flex-container-img"></div>
<h6 class="q-five-flex-title">Eficiencia</h6>
<p class="q-five-flex-content">Los vehículos eléctricos son más eficientes que los vehículos tradicionales.</p>
</div>

<div class="q-five-flex-entry">
<div class="q-five-flex-img-container-one"><img src="assets/img/pyr-p5-i6.png" alt="confort" class="q-five-flex-container-img"></div>
<h6 class="q-five-flex-title">Confort</h6>
<p class="q-five-flex-content">Viajar en vehículos eléctricos es más cómodo, debido a la ausencia de vibraciones o ruido del motor.</p>
</div>
</div>

<p>La autonomía, la infraestructura del hidrógeno y el tiempo de carga y el tamaño y capacidad de la batería son los principales desafíos
que tienen ambas tecnologías para implementarse ampliamente.</p>
        `), new SubtitleEntryImpl('TCO e importancia', `
<p>El TCO (Total Cost of Ownership) es el Costo Total de Propiedad y, en el caso de camiones eléctricos, incluye tanto el gasto capital de
comprar el vehículo como todos los costos operativos durante la vida útil o el período de propiedad. La paridad de costos se produce si el
TCO de la tecnología alternativa, eléctrico a batería (BEV) o a celda de combustible (FCEV), es el mismo o menor en comparación a la
tecnología convencional.</p>
<p>Si bien el concepto de TCO parece sencillo, la aplicación no es simple y los tomadores de decisiones pueden tener problemas con preguntas
sobre qué elementos de costo incluir y cómo obtener datos confiables. Además, muchos elementos son muy inciertos o desconocidos, incluidos
los precios futuros del combustible y la electricidad, el uso futuro y la vida útil de los vehículos, el rendimiento y la confiabilidad de
las nuevas tecnologías, y los incentivos y las políticas fiscales existentes o pendientes.</p>
<p>Si bien el TCO es una herramienta útil para el análisis, deben considerarse la disponibilidad de capital y las necesidades operativas de
la flota. Si una nueva tecnología tiene un TCO más bajo, pero cuesta el doble, comprar la nueva tecnología puede significar renunciar a un
segundo reemplazo de vehículo necesario, por ejemplo.</p>
<p>Del TCO se puede desprender un análisis de tiempo de recuperación o “payback”, que depende en gran medida del segmento y del mercado. Los
camiones eléctricos tienen un costo inicial más alto, pero también ofrecen nuevas oportunidades para que las empresas de transporte mejoren
la productividad y abran nuevas áreas de negocio. Los bajos costos variables conducirán a una mayor rentabilidad cuanto más se use el
camión. Una vez amortizada la alta inversión inicial, los camiones eléctricos tienen bajos costos variables, lo que también tiene un impacto
positivo en la rentabilidad.</p>
<p>En el marco de este proyecto, se ha desarrollado una herramienta TCO para uso público en donde el usuario podrá conocer todos los costos
de adquirir un camión eléctrico comparado con un camión convencional.</p>
        `), new SubtitleEntryImpl('Costos durante el ciclo de vida del camión', `
<ul class="tco-costs-list">
<li class="tco-cost-item">Adquisición</li>
<li class="tco-cost-item">Patente</li>
<li class="tco-cost-item">Seguro</li>
<li class="tco-cost-item">Batería extra (si corresponde)</li>
<li class="tco-cost-item">Mantenimiento</li>
<li class="tco-cost-item">Combustible (energía o hidrógeno)</li>
</ul>

<a href="/camiones-tco/calculadora-intro" class="tco-calculator-link">
<p class="calculator-link-text">Revisa la calculadora TCO</p>
<span class="right-pointing-arrow"></span>
</a>
        `), new SubtitleEntryImpl('Referencias', `
<ul class="references-list">
<li>Transport & Environment, “Comparison of hydrogen and battery electric trucks,” no. June, pp. 1–14, 2020, [Online]. Available:
https://www.transportenvironment.org/sites/te/files/publications/2020_06_TE_comparison_hydrogen_battery_electric_trucks_methodology.pdf.</li>
<li>“Why Electric trucks? Volvo Trucks.” https://www.volvotrucks.com/en-en/trucks/alternative-fuels/electric-trucks/faq.html
(accessed Aug. 05, 2021).</li>
<li>“The Future Lies In : EV? or FCEV? – Hyundai Motor Group TECH.” https://tech.hyundaimotorgroup.com/article/the-future-liesin-ev-or-fcev/
(accessed Sep. 10, 2021).</li>
<li>J. A. Sanguesa, V. Torres-Sanz, P. Garrido, F. J. Martinez, and J. M. Marquez-Barja, “A Review on Electric Vehicles: Technologies and
Challenges,” Smart Cities, vol. 4, no. 1, pp. 372–404, 2021, doi: 10.3390/smartcities4010022.</li>
<li>“Medium- and Heavy-Duty Vehicle Electrification An Assessment of Technology and Knowledge Gaps Medium- and Heavy-Duty Vehicle
Electrification,” no. December 2019, 2020.</li>
</ul>
        `)]
      }
    ],

    [
      {
        title: 'La batería del camión, ¿es un problema?',
        subtitles: [new SubtitleEntryImpl('Tipos de baterías', `
<p>La mayoría de las baterías para camiones eléctricos utilizan una de las combinaciones químicas de ion-litio disponibles comercialmente.
Estas y otras combinaciones están actualmente en el centro de muchos esfuerzos de investigación y están logrando cambios considerables en el
rendimiento de la batería. Las principales combinaciones químicas difieren en el cátodo y son las siguientes:</p>

<div class="q-six-grid-container">
<div class="q-six-grid-header">Material del cátodo</div>
<div class="q-six-grid-header">Densidad de energía (Wh/kg)</div>
<div class="q-six-grid-header">Ciclos de carga y descarga</div>
<div>óxido de litio-cobalto (LCO)</div>
<div>150-200</div>
<div>500-1000</div>
<div>óxido de litio-manganeso (LMO)</div>
<div>100-150</div>
<div>300-700</div>
<div>óxido de litio-níquelmanganeso-cobalto (LiNMC)</div>
<div>150-220</div>
<div>1000-2000</div>
<div>fosfato de hierro-litio (LiFePO4)</div>
<div>90-120</div>
<div>1000-2000</div>
<div>óxido de litio-níquel-cobaltoaluminio (LiNCA)</div>
<div>200-260</div>
<div>~500</div>
</div>

<p>Las baterías de ion-litio proporcionan altas densidades de energía. En la Tabla se pueden apreciar las propiedades energéticas de cada
material junto con la esperanza de vida de los ciclos de carga y descarga. Es a través de estos tipos de baterías, las emisiones de
producción, la densidad de energía y la esperanza de vida que se produce un compromiso entre la autonomía del vehículo y la vida útil de la
batería.</p>

<p>Mirando hacia el futuro, se espera una gran mejoría en la densidad de energía y en el ciclo de vida de las baterías de ion-litio. Además,
existe también una posibilidad de utilizar nuevas combinaciones químicas como el titanato de litio, ion-sodio e ionaluminio, que podrían
tener una densidad de energía y esperanza de vida superiores a las convencionales.</p>
        `), new SubtitleEntryImpl('Características de las baterías', `
<p></p>
<ul class="q-six-sq-two-list">
<li>
<h6 class="q-six-sq-two-list-title">Capacidad</h6>
<p class="q-six-sq-two-list-text">La capacidad de la batería representa la cantidad máxima de energía que se puede extraer de la batería
bajo ciertas condiciones específicas. Esta unidad puede expresarse en amperios hora (Ah) o en vatios hora (Wh), aunque esta última es la más
utilizada por los vehículos eléctricos.</p>
</li>
<li>
<h6 class="q-six-sq-two-list-title">Estado de carga</h6>
<p class="q-six-sq-two-list-text">Se refiere al nivel de la batería con respecto a su capacidad al 100%.</p>
</li>
<li>
<h6 class="q-six-sq-two-list-title">Densidad de energía</h6>
<p class="q-six-sq-two-list-text">Es la energía que una batería puede suministrar por unidad de volumen (Wh/L). Obtener la mayor densidad de
energía posible es otro aspecto importante en el desarrollo de las baterías, es decir, que con el mismo tamaño y peso una batería es capaz
de acumular una mayor cantidad de energía.</p>
</li>
<li>
<h6 class="q-six-sq-two-list-title">Energía específica</h6>
<p class="q-six-sq-two-list-text">La energía que una batería puede proporcionar por unidad de masa (Wh/kg). Algunos autores también se
refieren a esta característica como densidad de energía, y se puede especificar en Wh/L o Wh/kg.</p>
</li>
<li>
<h6 class="q-six-sq-two-list-title">Ciclos de carga</h6>
<p class="q-six-sq-two-list-text">Un ciclo de carga se completa cuando la batería se ha usado o cargado al 100%. También puede verse como
el ciclo desde que se carga la batería la totalidad de su capacidad y posteriormente se utiliza la energía igual a la capacidad de la
batería.</p>
</li>
<li>
<h6 class="q-six-sq-two-list-title">Vida útil</h6>
<p class="q-six-sq-two-list-text">Se mide en el número de ciclos de carga que puede contener una batería. El objetivo es obtener baterías
que puedan soportar un mayor número de ciclos de carga y descarga.</p>
</li>
<li>
<h6 class="q-six-sq-two-list-title">Resistencia interna</h6>
<p class="q-six-sq-two-list-text">Los componentes de las baterías no son conductores 100% perfectos, lo que significa que ofrecen cierta
resistencia a la transmisión de electricidad. Durante el proceso de carga, parte de la energía se disipa en forma de calor (es decir,
pérdida térmica). El calor generado por unidad de tiempo es igual a la potencia perdida en la resistencia, por lo que la resistencia interna
tendrá un mayor impacto en cargas de alta potencia. Por lo tanto, se perderá más energía durante los procesos de carga rápida en comparación
con los lentos. Por esto, es muy importante que las baterías puedan soportar una carga rápida y temperaturas más altas inducidas debido a la
resistencia interna. Además, la disminución de esta resistencia puede reducir el tiempo de carga que se requiere, que es uno de los
inconvenientes más importantes de este tipo de vehículos en la actualidad.</p>
</li>
<li>
<h6 class="q-six-sq-two-list-title">Eficacia</h6>
<p class="q-six-sq-two-list-text">Es el porcentaje de potencia que ofrece la batería en relación con la energía cargada.</p>
</li>
</ul>
        `), new SubtitleEntryImpl('Tiempo de carga de las baterías', `
<p>Los BEV almacenan energía en las baterías, medida en kWh, que luego es utilizada por el motor eléctrico. El factor determinante de la
velocidad de carga de las baterías de estos vehículos es la potencia eléctrica suministrada. Es medida en kW, e indica cuanta energía por
unidad de tiempo se le está suministrando a la batería. A partir de estos dos datos, y con la siguiente fórmula, se puede estimar el tiempo
de carga del vehículo:</p>
<div class="q-six-sq-three-img-container"><img src="assets/img/pyr-p6-i1.png" alt="fórmula tiempo de carga"></div>
<p>Por ejemplo, si se carga un camión BYD T6, que tiene una batería con capacidad para 120 kWh, con una potencia de 96 kW, se estima que se
cargará de 0% a 100% en 1,25 horas.</p>
<p>Esta fórmula se cumple aproximadamente entre el 20% y el 90% del estado de carga (State of Charge: SoC), mientras que fuera de estos
valores el tiempo es más prolongado.</p>
        `), new SubtitleEntryImpl('Variables que afectan la autonomía de las baterías', `
<p>
<ul class="q-six-sq-four-list">
<li>
<h6 class="q-six-sq-four-list-item-title">Relación autonomía – peso de la batería</h6>
<p>En comparación con la carga útil de referencia, una carga útil baja mejora la autonomía de conducción entre un 32% y un 36% para
cualquier tamaño de batería. Por el contrario, se observa una reducción menos sustancial del rango de conducción para camiones con carga
completa, que oscila entre el 6% y el 13%.</p>
</li>

<li>
<h6 class="q-six-sq-four-list-item-title">Efectos de la temperatura en la autonomía</h6>
<p>Las necesidades térmicas de los vehículos eléctricos pueden tener un impacto considerable en la autonomía de conducción, especialmente en
condiciones meteorológicas extremas. Un estudio realizado por el ICCT (International Council on Clean Transportation) compara dos
temperaturas extremas (-7°C y 35°C) con una temperatura de clima moderado de 15°C para tamaños de batería desde 300kWh hasta 1000kWh.</p>
</li>

<li>
<h6 class="q-six-sq-four-list-item-title">Efectos de la temperatura en la autonomía</h6>
<p>Las necesidades térmicas de los vehículos eléctricos pueden tener un impacto considerable en la autonomía de conducción, especialmente en
condiciones meteorológicas extremas. Un estudio realizado por el ICCT (International Council on Clean Transportation) compara dos
temperaturas extremas (-7°C y 35°C) con una temperatura de clima moderado de 15°C para tamaños de batería desde 300kWh hasta 1000kWh.</p>
<p>Los efectos en la autonomía es una consecuencia directa de la dependencia de las propiedades eléctricas de la batería de la temperatura y
del consumo de energía de la batería y los sistemas de gestión térmica de la cabina para mantener una temperatura de 20 ° C.</p>
<p>Para tamaños de batería más grandes, el sistema de gestión de la batería (TMS: termal management system) de la batería requiere más
energía para mantener la temperatura de las celdas de la batería dentro del rango deseado, lo que resulta en un mayor impacto en el rango de
conducción. Las condiciones climáticas cálidas y frías registran un impacto similar en el rango de conducción a pesar de que la diferencia
de temperatura entre la temperatura ambiente y la temperatura establecida en la cabina para un escenario de clima frío es mayor. Esto se
debe principalmente a la diferencia en el coeficiente de rendimiento del circuito de acondicionamiento térmico de la batería.</p>
<div class="q-six-sq-four-blue-box">
<p>Ejemplos de % de reducción de la autonomía para distintas temperaturas:</p>
<ul class="q-six-sq-four-blue-box-list">
<li>Batería de 300kWh --- 3% para -7°C y 35°C</li>
<li>Batería de 1000kWh --- 8% con 35°C y 9% con -7°C</li>
</ul>
</div>
</li>
</ul>
        `), new SubtitleEntryImpl('Costo de baterías y tendencias', `
<p>Investigaciones del Laboratorio Nacional de Lawrance Berkeley, la Universidad de California-Los Ángeles y la Universidad de
California-Berkeley muestran que los costos en las baterías de camiones han disminuido un 85% en la última década y que estos costos tienden
a seguir bajando. Esta tendencia de costos es la razón principal por la que la tecnología eléctrica de baterías tiene actualmente una
ventaja sobre la otra opción líder de cero emisiones, los camiones de celda de combustible de hidrógeno, que carecen de una amplia
infraestructura de reabastecimiento de combustible y enfrentan otros desafíos para la adopción a gran escala.</p>
<p>Los precios internacionales de los paquetes de baterías son actualmente de 135 USD por kilovatio-hora (kWh), alcanzables para los
fabricantes que producen camiones eléctricos a escala. Aun así, actualmente el precio un camión eléctrico es mayor al precio de un camión
diésel, pero los costos iniciales continuarán cayendo a medida que las baterías se vuelvan más económicas. Se espera que los precios
promedio de las baterías alcancen los 60 USD/kWh entre 2025 y 2030. Esta disminución, junto con las mejoras en el diseño de los vehículos y
otras políticas habilitadoras, significa que los camiones eléctricos podrían ofrecer hasta un 50% menos de costo total de propiedad por
milla en comparación con los camiones diésel convencionales.</p>
        `), new SubtitleEntryImpl('Vida útil de las baterías', `
<p>La depreciación de las baterías eléctricas tiene un impacto significativo en la depreciación física de los camiones eléctricos. La
investigación de la confiabilidad de la batería de potencia implica la estimación del estado de carga (SOC) y la estimación del estado de
salud (SOH).</p>
<p>El SOH es un índice que se puede calcular como:</p>
<div class="q-six-sq-six-img-container"><img src="assets/img/pyr-p6-i2.png" alt="fórmula SOH"></div>
<p>Donde Rt es la autonomía estimada en km del viaje número t y R0 es la autonomía estimada en km cuando el camión fue cargado por primera
vez por el usuario.</p>
<div class="q-six-sq-six-blue-box-container">
<div class="q-six-sq-six-blue-box">
<p>Los principales factores que afectan el SOH son:</p>
<ul class="q-six-sq-six-list">
<li>Tiempo</li>
<li>Temperaturas elevadas</li>
<li>Operar en niveles altos y bajos de batería</li>
<li>Cargar el camión con alta intensidad</li>
<li>Uso: los ciclos de la batería</li>
</ul>
</div>
</div>
<p>Según las descripciones de los fabricantes y la literatura existente, una vez que las baterías para vehículos eléctricos alcanzaron el
70-80% de su capacidad nominal, se considera que su función como baterías de primera vida para vehículos eléctricos ha llegado al final. La
razón principal es que este tipo de baterías reducirán el kilometraje y la velocidad. Se estima que esta degradación ocurre después de 5-8
años de uso o equivalente a 160.000km de viaje. Sin embargo, las baterías de vehículos eléctricos retiradas, incluso con menor SOH, podrían
reutilizarse en otras aplicaciones, como hogares residenciales o variación de energía en plantas fotovoltaicas a escala de red. Se estima
que pueden funcionar otros 7 a 10 años en estas aplicaciones antes de llegar al final de su vida útil y sea necesario reciclarlas.</p>
<p>Vale la pena mencionar que no todos los propietarios de vehículos eléctricos cambiarán sus baterías a pesar de que el rendimiento de la
batería cae por debajo del 70-80% de su capacidad original, ya que es posible que no necesiten la capacidad total para llegar a sus
destinos. Sin embargo, eventualmente, todas las baterías de EV deben ser reemplazadas.</p>
        `), new SubtitleEntryImpl('Disposición de las baterías', `
<p>La promoción de la electromovilidad conlleva el ingreso al país de nuevas tecnologías de baterías, siendo la más utilizada en la
actualidad la tecnología de ion de litio. Esto requiere de un marco regulatorio para el adecuado manejo de dichas baterías, una vez que
cumplan su vida útil en el vehículo eléctrico.</p>
<p>La adecuación de la normativa de baterías usadas actual (Decreto 373/003), la cual regula el manejo y disposición final de baterías
plomo-ácido, es una de las iniciativas que el Proyecto MOVÉS realiza en conjunto con la Dirección Nacional de Medio Ambiente (DINAMA).</p>
<p>En este sentido, ya fue elaborada una propuesta de reglamentación base, la cual se encuentra a la espera de revisiones para ser
aprobada.</p>
<p>Uno de los puntos clave es la instrumentación de la responsabilidad extendida del importador/fabricante de la batería, luego de que esta
cumplió su vida útil. En caso de no existir capacidades nacionales para una adecuación y segunda vida, o eventual reciclaje y disposición
final, el importador/fabricante de la batería deberá asegurarse de que dicha valorización sea realizada en el exterior.</p>
        `), new SubtitleEntryImpl('Referencias', `
<ul class="references-list">
<li>Basma, H., Beys, Y., Rodríguez, F., 2021. Battery electric tractor-trailers in the European Union: A vehicle technology analysis, ICCT
Working Paper</li>
<li>European Environment Agency, 2018. Electric vehicles from life cycle and circular economy perspectives: TERM 2018: Transport and
Environment Reporting Mechanism (TERM) report, EEA Report N°13/2018.</li>
<li>Forbes, 2021. Cheap Batteries Could Soon Make Electric Freight Trucks 50% Cheaper To Own Than Diesel [WWW Document]. URL
https://www.forbes.com/sites/energyinnovation/2021/03/16/plummeting-battery-prices-mean-electric-freight-truckscould-be-50-cheaper-to-own-than-diesel/?sh=2b711f14418c
(accessed 10.27.21). diesel/?sh=2b711f14418c (accessed 10.27.21).</li>
<li>Haram, M.H.S.M., Lee, J.W., Ramasamy, G., Ngu, E.E., Thiagarajah, S.P., Lee, Y.H., 2021. Feasibility of utilising second life EV
batteries: Applications, lifespan, economics, environmental impact, assessment, and challenges. Alexandria Eng. J. 60, 4517–4536.
https://doi.org/10.1016/j.aej.2021.03.021</li>
<li>Li, H., Chen, C., 2020. Research on Residual Value Evaluation of Battery Electric Vehicles Based on Replacement Cost Method. IOP Conf.
Ser. Earth Environ. Sci. 461. https://doi.org/10.1088/1755-1315/461/1/012027</li>
<li>Proyecto MOVÉS, 2021. Reglamentación de baterías - Proyecto MOVÉS [WWW Document]. Reglam. baterías. URL
https://moves.gub.uy/iniciativa/reglamentacion-de-baterias/ (accessed 12.28.21).</li>
<li>What can 6,000 electric vehicles tell us about EV battery health? | Geotab [WWW Document], n.d. URL
https://www.geotab.com/blog/ev-battery-health/ (accessed 10.5.21)</li>
</ul>
        `)]
      },
      {
        title: '¿Qué debo tener en cuenta si decido comprar un camión eléctrico a batería?',
        subtitles: [new SubtitleEntryImpl('Incentivos en Uruguay', `
<p>Uruguay actualmente incentiva la movilidad eléctrica y la incorporación de energéticos alternativos con el objetivo de lograr la
soberanía energética, disminuir las emisiones atmosféricas y la contaminación sonora. Uruguay presenta condiciones favorables para el
desarrollo del transporte de carga con camiones eléctricos en cuanto a generación e infraestructura, y es por esta razón que se han
desarrollado políticas que promueven la comercialización de esta tecnología.</p>
<p>A continuación, se listan las iniciativas que suponen beneficios económicos para empresas, producto de la incorporación del transporte
utilitario eléctrico:</p>
<ul class="q-seven-sq-one-list">
<li>
<h6 class="q-seven-sq-one-list-title">Reducción del Impuesto Específico Interno (IMESI) (Decreto 246/012) para híbridos y eléctricos</h6>
<p class="q-seven-sq-one-list-text">Transporte utilitario eléctrico: IMESI 2,3%, lo cual es menor que todos los otros utilitarios. Los
vehículos eléctricos tributan entre un 75% y 95% menos de IMESI que los vehículos convencionales.</p>
</li>
<li>
<h6 class="q-seven-sq-one-list-title">Ley de Promoción de Inversiones (Decreto 02/12)</h6>
<p class="q-seven-sq-one-list-text">La ley otorga beneficios a proyectos cuya inversión genere beneficios en diferentes sectores. En
particular, si la empresa presenta un proyecto donde hay inversión en vehículos utilitarios eléctricos, la misma se reduce desde un 30%
hasta más de un 70% a través de la exoneración de IRAE (Impuesto a las Rentas de las Actividades Económicas). En los casos más auspiciosos
el retorno del proyecto supera el 40%.</p>
</li>
<li>
<h6 class="q-seven-sq-one-list-title">Certificados de eficiencia energética</h6>
<p class="q-seven-sq-one-list-text">Instrumento económico del MIEM (Ministerio de Industria, Energía y Minería) para premiar las medidas de
eficiencia energética realizadas. Beneficio económico: del 3% a 30% de la inversión.</p>
</li>
<li>
<h6 class="q-seven-sq-one-list-title">Exoneración de tasa global arancelaria (TGA) (Decreto 410/016)</h6>
<p class="q-seven-sq-one-list-text">Transporte de mercancías solo con motor eléctrico: 0% TGA. Esto se debe a que Uruguay no tiene
producción nacional de vehículos eléctricos, por lo que se promovió la importación con la eliminación de los aranceles de importación
(de 23% a 0%).</p>
</li>
<li>
<h6 class="q-seven-sq-one-list-title">Descuento comercial UTE</h6>
<p class="q-seven-sq-one-list-text q-seven-sq-one-list-special-text">Cambio de potencia contratada sin costo.
<ul class="q-seven-sq-one-list-two">
<li>Tarifa en valle (00:00 a 7:00) al 50% en las estaciones de recarga de UTE.</li>
<li>Tarifas mediano consumidor y tarifa doble residencial al 50% en horario fuera de punta (18 a 22).</li>
</ul></p>
</li>
<li>
<h6 class="q-seven-sq-one-list-title">Bonificación de seguros del BSE (Banco de Seguros del Estado) para camiones eléctricos</h6>
<p class="q-seven-sq-one-list-text">15% de descuento en tarifa de eléctricos.</p>
</li>
<li>
<h6 class="q-seven-sq-one-list-title">Créditos verdes</h6>
<p class="q-seven-sq-one-list-text">A través de MOVÉS, instituciones financieras como BBVA, Itaú y HSBC crearon los créditos verdes,
específicos para la compra de vehículos eléctricos. Este incentivo busca reducir la barrera de compra a través de una mejora en las
condiciones de financiamiento de estos vehículos.</p>
</li>
</ul>
        `), new SubtitleEntryImpl('Infraestructura existente en Uruguay', `
<p>En Uruguay los conectores estándar son los conectores Tipo 2. La norma que define los estándares con respecto a fichas, tomacorrientes,
conectores del vehículo y conexiones de entrada del vehículo eléctrico (VE) en Uruguay es UNIT 1234:2020. Además, se utilizan las normas
UNIT-IEC 61851-1:2017, enfocada en tipos de comunicación entre el SAVE y VE, y la norma UNITIEC 62196-1:2014, enfocada en tipos de
conectores de VE. Sin embargo, dado que en Uruguay se importaron vehículos antes de que el estándar de conectores tomara vigencia, existe
una minoría de VEs que tienen otro tipo de conector.</p>
<p>Uruguay fue el primer país latinoamericano en poseer un corredor de carga para VEs. Hoy en día (marzo, 2021) se tienen instalados 70
cargadores en vía pública, uno de ellos siendo de corriente continua. Además, se tienen disponibles 220 cargadores CA y 6 CC en el
territorio uruguayo, cuya instalación se retrasó debido a la pandemia del COVID-19. Para informarse acerca de las ubicaciones de estaciones
de carga, el estado de sus cargadores (ocupado/ disponible) y la potencia que son capaces de entregar se recomienda la aplicación UTE
Mueve.</p>
        `), new SubtitleEntryImpl('Infraestructura - cómo prepararse', `
<p>El método más popular para cargar VEs es a través de un cable. Para lograr esto se instala un Sistema de Alimentación de Vehículos
Eléctricos (SAVE), también llamado estación de carga, entre la red eléctrica y el vehículo. Su función es gestionar la carga, con énfasis en
la seguridad del usuario.</p>
<p>El objetivo del SAVE es gestionar la carga del vehículo eléctrico, a partir de una comunicación entre ambos. Se encarga de proteger al
usuario haciendo circular energía solo cuando el cargador está enchufado al vehículo y cuando no hay problemas de aislación. Además, protege
al vehículo de tres maneras: detectando el SoC, haciendo pasar una corriente que sea soportada tanto por el cargador como por el vehículo y
detectando problemas de hardware. De esta manera se evitan daños a las baterías, cortocircuitos y posibles incendios.</p>
<p>Esto significa que, si el cargador es de 100 kW, pero por el vehículo sólo pueden circular 50 kW, el SAVE hará circular solamente 50 kW.
En el caso inverso, en el que el vehículo pueda tomar hasta 100 kW, pero la estación de carga solo puede transmitir 50 kW, el vehículo
tomará 50 kW.</p>
<p>Integrados a un sistema de carga inteligente también pueden informar el SoC al sistema, para optimizar la carga de una flota de camiones
eléctricos, por ejemplo. Algunas de las operaciones que se podrían lograr con la carga inteligente son optimizar la secuencia en que se
cargan los vehículos, controlar la potencia consumida y cargar en horarios en los cuales la tarifa es menor.</p>
<h6 class="q-seven-sq-three-title">Modos de carga</h6>
<div class="q-seven-sq-three-table">
<div class="q-seven-sq-three-theader">Tipo</div>
<div class="q-seven-sq-three-theader">Potencia</div>
<div class="q-seven-sq-three-theader">Modo</div>
<div>Lenta (emergencia)</div>
<div>2,2kW</div>
<div>Schuko</div>
<div>Estándar</div>
<div>de 3,7 a 7,4 kW</div>
<div>SAVE</div>
<div>Semi rápida</div>
<div>hasta 22 kW</div>
<div>SAVE</div>
<div>Rápida</div>
<div>hasta 43 kW</div>
<div>SAVE</div>
<div>Ultra rápida</div>
<div>120 kW</div>
<div>SAVE</div>
</div>

<p>La carga lenta a 2,2kW la admiten únicamente algunos vehículos eléctricos de pasajeros.</p>
        `), new SubtitleEntryImpl('Tipos de cargadores', `
<div class="q-seven-sq-four-table">
<div class="q-seven-sq-four-theader">Nombre</div>
<div class="q-seven-sq-four-theader">Modo 3</div>
<div class="q-seven-sq-four-theader">Modo 4</div>
<div class="q-seven-sq-four-timg-one q-seven-sq-four-timg"><img src="assets/img/pyr-p7-i1.png" alt="cargador batería"></div>
<div class="q-seven-sq-four-timg-two q-seven-sq-four-timg"><img src="assets/img/pyr-p7-i2.png" alt="cargador batería"></div>
<div class="q-seven-sq-four-table-col-one">Descripción</div>
<div>Conexión entre VE y SAVE, el cual se encuentra conectado permanentemente a la red de suministro, con una función piloto de control que
se extiende desde el SAVE hasta el VE.</div>
<div>Conexión entre VE y SAVE de DC o AC con una función de piloto de control que se extiende desde el SAVE de DC al VE.</div>
<div class="q-seven-sq-four-table-col-one">Tipo de corriente</div>
<div>CA</div>
<div>CC</div>
<div class="q-seven-sq-four-table-col-one">Potencia (kW)</div>
<div>8 - 44</div>
<div>44+</div>
<div class="q-seven-sq-four-table-col-one">Costo de adquisición</div>
<div>3000</div>
<div>18000</div>
</div>

<p>Debido a que los camiones eléctricos a batería necesitan de mucha energía para ser cargados, los SAVE utilizados son los de modo 3 y
modo 4. La decisión entre un tipo o el otro depende de cuánto tiempo tenga disponible el vehículo para ser cargado. En operaciones de
retorno a base se suele realizar la carga con un modo 3 durante la noche, mientras que, en operaciones más intensivas con menos tiempos
muertos, se utilizan los cargadores modo 4. Dado que los vehículos personales tienen limitada la potencia que pueden recibir, y por tanto
no pueden aprovechar al máximo las ventajas de los de modo 4, estos son utilizados principalmente por VEs pesados, de larga distancia o con
baterías grandes.</p>
<p>Otro comentario importante es que los precios mencionados en la tabla solo corresponden al costo de adquisición. Los de modo 4 tendrán el
mayor costo adicional de entre los tres mencionados debido a su gran impacto en la red eléctrica (costo de operación) y a su mayor tamaño
(costo de instalación).</p>
<p>Además de haber diferentes tipos de SAVE se tienen diferentes tipos de conectores entre vehículo y SAVE. Estos están estandarizados y por
lo tanto permiten una clasificación más estricta que en el caso anterior.</p>

<p class="q-seven-sq-four-special-text">Proveedores SAVE:
<a href="https://movilidad.ute.com.uy/carga.html?tab=red-de-carga" target="_blank">
Haga click aquí para ver la lista de proveedores SAVE en Uruguay.</a>
</p>
        `), new SubtitleEntryImpl('Referencias', `
<ul class="references-list">
<li>Carga de vehículos - Movilidad Eléctrica - UTE. (n.d.). Retrieved March 19, 2021, from https://movilidad.ute.com.uy/carga.htm</li>
<li>Hall, D., & Lutsey, N. (2019). Estimating the infrastructure needs and costs for the launch of zero-emission trucks. The International
Council on Clean Transportation, August, 1–31. https://theicct.org/sites/default/files/publications/ICCT_EV_HDVs_Infrastructure_20190809.pdf</li>
<li>Regis, N., Heinrich, M. T. E. E., Kelch, F., Magne, P., Emadi, A., Zhang, M., Huang, Q., Liu, S., Li, H., Khoury, J., Database, F. P.,
O’Shaughnessy, E., Cutler, D., Ardani, K., Margolis, R., Li, N., Sun, Y., Yu, J., Li, J. C., … Wu, J. (2017). Electrification of the
Transport System: Studies and Reports. Renewable and Sustainable Energy Reviews, 10(6), 1–49.</li>
<li>UNIT-IEC. (2017). UNIT-IEC 61851-1:2017.</li>
<li>UNIT. (2020). UNIT 1234:2020.</li>
</ul>
        `)]
      },
      {
        title: '¿Qué modelos de camiones a batería hay disponibles en Uruguay?',
        subtitles: [new SubtitleEntryImpl('Modelos en Uruguay', `
<div data-truck-models-in-uruguay class="q-eight-content-container">
<div class="q-eight-pdf-container">
<iframe src="assets/files/modelos-de-camiones-a-bateria.pdf" type="application/pdf"><div></div></iframe>
</div>

<a href="assets/files/modelos-de-camiones-a-bateria.pdf" class="q-eight-pdf-flex-container" download="Modelos de camiones eléctricos">
<img src="assets/img/pyr-p8-i1.png" alt="ícono de descarga" class="q-eight-pdf-img-one">
<img src="assets/img/pyr-p8-i2.png" alt="ícono pdf" class="q-eight-pdf-img-two">
<span>Modelos de camiones eléctricos.pdf</span>
</a>
</div>
        `)]
      },
      {
        title: '¿Cómo mejoro la operativa diaria?',
        subtitles: [new SubtitleEntryImpl('Frenado regenerativo', `
<p>Cualquier vehículo en movimiento tiene mucha energía cinética y cuando se aplican los frenos para desacelerar, toda esa energía debe ir a
alguna parte. En los vehículos convencionales con motor de combustión interna, los frenos a través de la fricción convierten la energía
cinética en calor que se disipa al ambiente. El frenado regenerativo utiliza al motor del vehículo eléctrico como generador para convertir
gran parte de la energía cinética perdida al desacelerar en energía almacenada en la batería. Esta energía es utilizada posteriormente
cuando se tiene que volver a acelerar.</p>
<p>Es importante aclarar que el frenado regenerativo no es un potenciador de autonomía, es decir, no aumenta la autonomía y no hace que los
vehículos sean más eficientes, sino que hace que sean menos ineficientes. La forma más eficiente de conducir sería a velocidad constante y
nunca tocar el freno dado que al frenar se elimina energía y luego se requiere energía para volver a llegar a la velocidad. De esta forma,
la mayor autonomía se obtiene nunca disminuyendo la velocidad, lo cual no puede ser real, ya que no se adapta a las operativas diarias. En
estos casos el frenado regenerativo elimina la ineficiencia del frenado y hace que el proceso de frenar tenga menos desperdicios
energéticos.</p>
        `), new SubtitleEntryImpl('Capacitación de choferes', `
<p>La forma en la que los vehículos eléctricos consumen energía ha sido estudiada en numerosos estudios debido al impacto que tiene tanto en
la vida útil de la batería del vehículo como en el costo de operarlo.</p>
<p>Si bien los camiones eléctricos son muy similares a los camiones a diésel convencionales en apariencia, el funcionamiento y la tecnología
es muy distinta y amerita que los conductores reciban una capacitación. Dicha capacitación se recomienda que sea tanto teórica como
práctica, a través de recorridos alrededor del camión y prácticas conduciendo. Además, se recomienda capacitación técnica básica enfocada en
cómo trabajar con componentes que están a alta tensión. En ocasiones los proveedores ofrecen y obligan a los conductores a tener esta
capacitación.</p>
<p>Los conductores pasan por un proceso de aprendizaje relativamente corto (pocas semanas) hasta que logran adaptarse y dominar la
tecnología. Aprender a manejar de forma eficiente y eficaz las novedades del frenado regenerativo, la carga y la autonomía en el uso
cotidiano de un vehículo eléctrico es esencial para lograr un comportamiento de conducción y experiencia del usuario optimizado.</p>

<div class="q-nine-sq-two-title-container"><p>Algunos consejos para maximizar la autonomía son:</p></div>
<ul class="q-nine-sq-two-list">
<li>No acelerar rápidamente porque se utiliza más corriente, lo cual reduce los kWh disponibles y a su vez reduce la autonomía</li>
<li>Utilizar el frenado regenerativo cuando sea posible</li>
<li>No usar el aire acondicionado cuando no sea necesario dado que, después de la aceleración, es el mayor consumidor de energía</li>
<li>Durante la conducción, no mantener el pie en el pedal del freno durante un largo tiempo. Eso puede causar fenómeno de
sobrecalentamiento, desgate y desperdicio de energía eléctrica</li>
</ul>

<div class="q-nine-sq-two-example-container">
<div class="q-nine-sq-two-example-header">
<hr>
<h6>Caso real</h6>
</div>

<div class="q-nine-sq-two-example-body">
<p>Se muestra la curva de aprendizaje de conductor de un camión eléctrico modelo T6A que realiza distribución urbana. En 25 días logró
llegar a un porcentaje alrededor del 25% y se mantuvo relativamente constante.</p>
<div class="q-nine-sq-two-img-container"><img src="assets/img/pyr-p9-i1.png" alt="gráfica de curva de aprendizaje"></div>
</div>
</div>
        `), new SubtitleEntryImpl('Seguridad', `
<p>Si bien los vehículos eléctricos tienen ciertas ventajas en materia de seguridad debido a que no llevan a bordo combustibles que son
inflamables y tóxicos, es importante tener en cuenta las propiedades físicas y químicas de las baterías y cómo se comportan en distintos
escenarios.</p>
<p>Las baterías de litio son las más comunes en los vehículos eléctricos debido a su alta energía y densidad de potencia, combinada con la
longevidad de la batería y la autonomía alcanzable. Estas baterías contienen materiales reactivos extremadamente energéticos y electrolitos
orgánicos inflamables. Esto supone una serie de problemas de seguridad que no se encuentra en vehículos convencionales e introduce riesgos
que deben comprenderse y gestionarse adecuadamente.</p>
<p>Las baterías contienen los tres elementos necesarios para producir un incendio a través de una reacción química: fuente de
calor/ignición, oxígeno y material combustible.</p>
<p>Las baterías tienen un rango de funcionamiento muy estrecho, si la celda se calienta debido a una carga rápida, una sobrecarga, un
cortocircuito o una deformación mecánica, como un impacto o un calentamiento externo, el sistema de iones de litio está diseñado para
ventilar el gas para evitar la explosión.</p>
<p>Si la temperatura alcanzara aproximadamente 150°C - 200°C, la batería se vuelve inestable y se puede dar lo que se conoce como fuga
térmica, un rápido aumento de temperatura interna que desencadena continuamente una variedad de descomposición y otras reacciones
electroquímicas exotérmicas asociadas. Las tasas de liberación de energía durante esta reacción aumentan con la temperatura, provocando
fugas de electrolito, generación de calor, formación rápida de gas y humo, rotura de celda, incendio, desmontaje rápido y, en el peor de los
casos, explosión de gas como resultado de la combustión del electrolito y otros componentes combustibles después de la ruptura del
confinamiento de la batería.</p>
<p>A pesar de que existen diversos riesgos vinculados al uso de baterías, también existen muchos riesgos cuando se utiliza unvvehículo a
combustión interna. Los proveedores de los vehículos eléctricos aseguran que cumplen con los mismos altos estándares de seguridad en choques
que otros vehículos y ofrecen los mismos sistemas de seguridad que los modelos diésel. En ocasiones también se agregan escenarios de choque
específicos para los vehículos eléctricos para verificar la resistencia a los choques de la instalación de la batería y otros componentes
eléctricos.</p>
        `), new SubtitleEntryImpl('Consideraciones sobre la carga de la batería', `
<p>
<ul class="q-nine-sq-four-list">
<li>No es recomendable que el estado de carga (SoC: State of Charge) de la batería esté por debajo de 20%</li>
<li>La carga de una batería es más eficaz cuando su SoC es bajo. La aceptación de la carga disminuye cuando la batería alcanza un SoC del
70% o más. Una batería completamente cargada ya no puede convertir la energía eléctrica en energía química y la carga debe reducirse para
que se apague.</li>
<li>Cargando una batería más allá del estado de carga completo convierte el exceso de energía en calor y gas y puede resultar en un depósito
de materiales no deseados. Además, la sobrecarga prolongada causa daño permanente.</li>
<li>Antes de iniciar la carga se debe inspeccionar que dentro del puerto de carga y el puerto del conector de carga del vehículo no haya
oxidación o corrosión. De haber oxidación o corrosión no se debe cargar dado que se puede ocasionar un cortocircuito o descarga
eléctrica.</li>
<li>No se debe desconectar el cargador con manos húmedas dado que puede ocasionar lesiones personales.</li>
<li>Cuando la temperatura ambiental es inferior a 0ºC, la duración de carga será más larga que el caso normal, la capacidad de carga es
relativamente baja.</li>
<li>Si el vehículo no se utiliza durante un largo tiempo, para extender la vida útil de la batería de energía, se recomienda cargarla y
conducir el vehículo una vez cada 3 meses.</li>
<li>Se debe utilizar el cargador correcto para la química de la batería, la mayoría de los cargadores sirven para sólo un tipo de
batería.</li>
<li>Un cargador de alto voltaje acorta el tiempo de carga, pero existen limitaciones en cuanto a la rapidez con la que se puede cargar una
batería. La carga ultrarrápida causa estrés.</li>
<li>Se debe observar la temperatura de carga. Las baterías de plomo ácido deben permanecer tibias al tacto; Las baterías de níquel se
calentarán hacia el final de la carga, pero deben enfriarse cuando estén "listas". El ion de litio no debe elevarse más de 10ºC por encima
de la temperatura ambiente cuando alcance la carga completa.</li>
</ul>
        `), new SubtitleEntryImpl('Costo de cargar la batería', `
<p>La disponibilidad de tiempo para cargar el camión depende de las operaciones de cada empresa. Idealmente y debido a los costos de las
tarifas eléctricas en Uruguay, se sugiere que la carga sea en la noche, donde las tarifas de energía eléctrica son las mínimas.</p>

<div class="q-nine-sq-six-example-container">
<div class="q-nine-sq-six-example-header">
<hr>
<h6>Ejemplo</h6>
</div>
<div class="q-nine-sq-six-example-body">
<p>A modo de ejemplo, si la empresa tiene tarifa de mediano consumidor 1 y posee un camión BYD T45C01 con una capacidad de batería de
100kWh, al cual carga en horario valle, el costo de cargar al camión será de:</p>
<p class="center-align">2,150$/kWh*105kWh=225,75$</p>
<p class="center-align">Con esta carga y un rendimiento de 2km/kWh se logra una autonomía de 210km.</p>
</div></div>
        `), new SubtitleEntryImpl('¿Qué pasa si me quedo sin batería en la ruta', `
<p>Los usuarios normalmente aprenden cómo funciona el camión y se sienten cómodos con la autonomía muy rápidamente. Si la carga de la
batería cae a cero mientras se conduce el camión, hay una cierta capacidad de reserva de energía disponible que ayudará al conductor a
llegar a un lugar seguro.</p>
        `), new SubtitleEntryImpl('¿Es confiable tener la flota 100% eléctrica?', `
<p>Para determinar si es confiable tener una flota 100% eléctrica se debe hacer un estudio de las distancias recorridas y los kilogramos
transportados históricamente, de forma de asegurarse de que las prestaciones de los camiones eléctricos sean suficientes. Los camiones
totalmente eléctricos son ideales para transportar mercancías dentro y alrededor de las ciudades en rutas predefinidas que incluyen un
regreso a la base de operaciones al final de la jornada laboral para cargarlas. La distribución, la construcción urbana y la recolección de
basura (a menudo para las autoridades municipales) son ejemplos de segmentos que han sido los primeros en adoptar camiones eléctricos.</p>
        `), new SubtitleEntryImpl('Referencias', `
<ul class="references-list">
<li>BU-401: How do Battery Chargers Work? - Battery University [WWW Document], 2021. URL
https://batteryuniversity.com/article/bu-401-how-do-battery-chargers-work (accessed 12.29.21).</li>
<li>Helmbrecht, M., Olaverri-Monreal, C., Bengler, K., Vilimek, R., Keinath, A., 2014. How electric vehicles affect driving behavioral
patterns. IEEE Intell. Transp. Syst. Mag. 6, 22–32. https://doi.org/10.1109/MITS.2014.2315758</li>
<li>Manual BYD T6A</li>
<li>Run On Less: Maintenance, Training & Safety [WWW Document], 2021. URL https://runonless.com/bootcamp/maintenancetraining-safety/
(accessed 12.4.21)</li>
<li>Why Electric trucks? Volvo Trucks [WWW Document], n.d. URL
https://www.volvotrucks.com/en-en/trucks/alternativefuels/electric-trucks/faq.html (accessed 8.5.21).</li>
</ul>
        `)]
      }
    ],

    [
      {
        title: '¿Cuándo va a ser viable el camión con celda de combustible de hidrógeno?',
        subtitles: [new SubtitleEntryImpl('Obtención y uso del hidrógeno', `
<p>El hidrógeno es el elemento más abundante en el universo, pero no se forma naturalmente por sí solo. Se puede encontrar en agua,
combustibles fósiles o biomasa. Para extraer el hidrógeno, se pueden utilizar procesos como, por ejemplo, la electrólisis o el reformado de
metano con vapor.</p>
<p>Como se mencionó en la pregunta 4, el hidrógeno se puede obtener de distintas fuentes. Si bien no existe una nomenclatura oficial, en la
industria se suele distinguir al hidrógeno según su origen con colores. Debajo se muestra la nomenclatura utilizada.</p>
<div class="q-ten-sq-one-img-container">
<img src="assets/img/pyr-p10-i1.png" alt="colores del hidrógeno">
</div>

<p>El hidrógeno es un portador de energía que no se consume, sólo se utiliza. Como se mencionó anteriormente, una forma de obtención del
hidrógeno es a partir de la electrólisis. Este es el proceso de obtención que interesa en Uruguay. Consiste en la división de la molécula de
agua (H2O) en dos átomos de hidrógeno y un átomo de oxígeno mediante la adición de energía eléctrica. En específico, es de interés para la
descarbonización del transporte que la energía eléctrica utilizada sea renovable. El oxígeno que se produce se libera a la atmósfera y el
hidrógeno se almacena en un tanque. Este hidrógeno se puede utilizar para llenar una celda de combustible de un vehículo. Cuando el vehículo
se encuentra en funcionamiento, su celda de combustible toma el hidrógeno almacenado y oxígeno de la atmósfera y produce energía eléctrica
para alimentar al motor eléctrico, agua y calor. La producción de hidrógeno mediante electrólisis requiere alrededor de 9 litros de agua por
kg de hidrógeno, por lo que debe considerarse una fuente estable de este recurso, que Uruguay, a diferencia de otros países de la región,
la tiene.</p>
        `), new SubtitleEntryImpl('Viabilidad del uso de hidrógeno como combustible en camiones eléctricos', `
<p>Las pilas de combustible de hidrógeno usan desde la década de 1960 en naves espaciales. En la década de 1970, empresas como Rocketdyne
investigaron posibles usos de transporte comercial para las pilas de combustible, incluidos trenes, barcos, autobuses y camiones y desde
2011 se están creando prototipos de camiones con pila de combustible de hidrógeno. Hoy en día ya se utilizan autos y ómnibus que funcionan
con esta tecnología.</p>
<p>La producción de hidrógeno es un tecnología madura, en el mundo actualmente se producen 100 millones de toneladas de hidrógeno y en su
mayoría se destina a la industria. Por ejemplo, en Estados Unidos se producen anualmente más de 10 millones de toneladas de hidrógeno
destinado a las industrias química, de fertilizantes y alimentaria.</p>
<p>Es claro que hay intereses en la producción de hidrógeno debido a la variedad de usos finales que pueden beneficiarse de su
disponibilidad. El hidrógeno para el transporte es sólo una pequeña parte de una economía de hidrógeno mucho mayor que se debe planificar.
Una economía del hidrógeno requiere un enfoque regional en el suministro de hidrógeno como energía para múltiples usos. Una vez que la
producción de hidrógeno a escala está disponible, estos múltiples usos finales se vuelven económicamente viables. El desafío es lograr la
producción de hidrógeno a gran escala.</p>
        `), new SubtitleEntryImpl('Avances a nivel país', `
<p>Actualmente se encuentra vigente el proyecto H2U que busca posicionar a Uruguay como productor y abastecedor de demandas locales y,
eventualmente, exportador de hidrógeno verde.</p>
<p>ANCAP impulsa este proyecto como una continuación del proyecto VERNE. El nuevo proyecto tiene tres focos principales: hacer un piloto
para transporte pesado de buses y camiones, producción de hidrógeno on-shore y producción de hidrógeno off-shore. Se busca la inversión de
privados para la generación de energía renovable dedicada a la producción de hidrógeno. De esta manera, el modelo de negocios va a ser
totalmente privado, debido a que las empresas deben aportar una flota vehicular de ómnibus y camiones y deberán mantener contratos de
demanda energética según sus necesidades. Se busca hacer un proyecto piloto con al menos diez ómnibus de nueve metros de largo o camiones de
17 toneladas, donde la distancia mínima que recorra la flota sea 3500km por día. Los inversionistas privados deberán instalar una planta
propia, aunque va a ser posible utilizar las instalaciones de ANCAP en la planta de Capurro. Además, estará vigente la Ley de Promoción de
Inversiones y se prevén apoyos públicos adicionales para fomentar las inversiones.</p>
<p>Un foco grande del proyecto es crear una granja eólica off-shore. Las investigaciones hechas por ANCAP demuestran que existen dos zonas
de aproximadamente 2000m2 cada una en donde las condiciones del viento y la profundidad son óptimas para la instalación de molinos. A partir
de licitaciones, se conceden bloques del territorio marítimo uruguayo para la generación de energía y la producción de hidrógeno in
situ.</p>
<div class="q-ten-sq-three-special-text-container"><span>
Links de interés:
<a href="https://www.gub.uy/ministerio-industria-energia-mineria/comunicacion/noticias/uruguay-lanza-proyecto-piloto-h2u-para-avanzar-hacia-produccion-hidrogeno" target="_blank">Lanzamiento del proyecto H2U</a>
y
<a href="https://www.gub.uy/ministerio-industria-energia-mineria/comunicacion/noticias/650-participantes-asistieron-lanzamiento-h2u-proyecto-uruguay-para-producir" target="_blank">Noticia</a>
</span></div>
<p>Jorge Ferriero, director de Hidrógeno en ANCAP, y Fernando Schaich, fundador de SEG Ingeniería, participaron de una mesa redonda sobre
hidrógeno, en donde analizaron el potencial que tiene Uruguay para el mercado del hidrógeno. Por un lado, Jorge sostuvo que Uruguay cuenta
con tres drivers para la producción de hidrógeno:</p>
<ol class="q-ten-sq-three-list">
<li>Ambiental: descarbonizar el mundo ya forma parte de la agenda social y de la agenda de los gobiernos.</li>
<li>Los costos van a ir bajando a medida que se va escalando la producción</li>
<li>Hay apetito de inversores en invertir en energías verdes como inversiones a largo plazo</li>
</ol>
<p>Además, Fernando Schaich, sostuvo que Uruguay presenta dos ventajas principales para la producción de hidrógeno verde:</p>
<ol class="q-ten-sq-three-list-two">
<li>Si bien cuando se compara con Chile o Brasil, Uruguay no es el mejor posicionado en generación de energía a partir de energía solar o
eólica, Uruguay tiene la ventaja de que tiene disponible ambas energías y que estas se complementan (generación baja de energía solar
coincide con generación alta de energía eólica y viceversa).</li>
<li>Uruguay es un mercado seguro y con bajo riesgo y tasas, lo cual es muy buscado por inversores.</li>
</ol>
<p>Tanto Fernando Schaich como Jorge Ferreiro estuvieron de acuerdo en que hasta que se empieza a producir y utilizar hidrógeno puede pasar
un período de tiempo de ocho a diez años.</p>
<div class="q-ten-sq-three-flex-container">
<div class="q-ten-sq-three-quote-container">
<blockquote>Uruguay llevó adelante una trayectoria destacada en la incorporación de energías renovables en su matriz de energía eléctrica,
que nos permite dar un paso más y posicionarnos en el mundo como un actor capaz de dar respuesta a las nuevas demandas energéticas a nivel
local y global. Para ello, vemos conveniente avanzar en el desarrollo del Hidrógeno Verde, que brinda la posibilidad dar respuesta a la
demanda de producciones más limpias y amigables con el medio ambiente, al tiempo que permitirá a largo plazo dar un nuevo destino a la
capacidad utilizable de generación eléctrica con la que cuenta el país</blockquote></div>
<div class="q-ten-sq-three-profile-container">
<div class="q-ten-sq-three-img-container">
<img src="assets/img/pyr-p10-i2.jpg" alt="Omar Paganini">
</div>
<p>Ing. Omar Paganini</p>
<p>Ministro de Industria, Energía y Minería</p>
</div>
</div>
        `), new SubtitleEntryImpl('Infraestructura en Uruguay', `
<p>En base a la pregunta anterior, actualmente no hay infraestructura ni de producción ni de distribución de hidrógeno en Uruguay. Sin
embargo, en este aspecto se centra el proyecto H2U.</p>
        `), new SubtitleEntryImpl('Referencias', `
<ul class="references-list">
<li>CHBC, 2018. CHBC Hydrogen FAQ 1–16.</li>
<li>Development, D. of P.I. and R., n.d. Frequently Asked Questions Renewable Hydrogen.</li>
<li>En Perspectiva, 2021. Hidrógeno verde: ¿Qué potencial tiene Uruguay en ese mercado? - YouTube [WWW Document]. URL
https://www.youtube.com/watch?v=QISm8gbicRc (accessed 12.29.21).</li>
<li>MIEM, 2021. Proyecto piloto H2U [WWW Document]. URL
https://www.gub.uy/ministerio-industria-energiamineria/comunicacion/noticias/uruguay-lanza-proyecto-piloto-h2u-para-avanzar-hacia-produccion-hidrogeno
(accessed 12.9.21).</li>
<li>NACFE, 2020. Making Sense of Heavy-Duty Hydrogen Fuel.</li>
</ul>
        `)]
      },
      {
        title: '¿Qué debo tener en cuenta si decido comprar un camión a hidrógeno?',
        subtitles: [new SubtitleEntryImpl('Modelos en Uruguay', `
<p>En Uruguay no hay oferta de camiones con celda de combustible de hidrógeno. Sin embargo, en la Pregunta 4 se listan los modelos de
camiones que existen mundialmente.</p>
        `), new SubtitleEntryImpl('Incentivos proyectados en Uruguay', `
<p>Aún no existen políticas de incentivos para la compra de camiones con celda de combustible de hidrógeno. Sin embargo, estos camiones son
clasificados como eléctricos porque funcionan con un motor eléctrico, por ende, los beneficios mencionados anteriormente para los camiones
eléctricos a batería deberían ser trasladables a estos camiones si no se indica lo contrario.</p>
        `), new SubtitleEntryImpl('Recarga de hidrógeno', `
<p>El hidrógeno, al igual que los combustibles fósiles, se deberá cargar en estaciones de servicio que suministren hidrógeno, ya sean
propias o públicas.</p>
<p>El tiempo que se demora en llenar el tanque de combustible es una de las mayores ventajas que tiene el hidrógeno frente a las
baterías. La siguiente tabla muestra el estado actual de autos y ómnibus y las predicciones de camiones para 2030 y 2050.</p>
<div class="q-eleven-sq-three-grid-container">
<div class="q-eleven-sq-three-grid-header q-eleven-sq-three-grid-col-two">Estado de vehículos particulares (2015)</div>
<div class="q-eleven-sq-three-grid-header">Estado de ómnibus (2017)</div>
<div class="q-eleven-sq-three-grid-header">Objetivo camiones pesados (2030)</div>
<div class="q-eleven-sq-three-grid-header">Objetivo camiones pesados (2050)</div>
<div class="q-eleven-sq-three-grid-col-one">Capacidad de tanque (kg)</div>
<div>2-5</div>
<div>32</div>
<div>60</div>
<div>60</div>
<div class="q-eleven-sq-three-grid-col-one">Autonomía (km)</div>
<div>320-520</div>
<div>480</div>
<div>1200</div>
<div>1200</div>
<div class="q-eleven-sq-three-grid-col-one">Tiempo de llenado de tanque (minutos)</div>
<div>5-6</div>
<div>22-24</div>
<div>10</div>
<div>6</div>
<div class="q-eleven-sq-three-grid-col-one">Tasa de llenado de tanque (kg/min)</div>
<div>0,35</div>
<div>1-1,5</div>
<div>8</div>
<div>10</div>
</div>
<p>Cabe destacar que, en las estaciones de carga, el nivel de presión de suministro de hidrógeno determina el tipo de vehículo que puede
hacer uso de la estación. Mientras que los vehículos de particulares tienen tanques de hasta 700 bar, los camiones y ómnibus, que cuentan con
mayor espacio disponible para los tanques, pueden operar con hidrógeno a 350 bar.</p>
        `), new SubtitleEntryImpl('Costo del hidrógeno y rendimiento', `
<p>Hay mucha incertidumbre y es muy pronto para estimar el precio que verá el consumidor en una estación de hidrógeno en Uruguay. Este
precio depende de diversos factores y entre ellos el principal es el costo de producción. Actualmente, según un estudio realizado por IEA
(International Energy Agency), producir el hidrógeno a partir de energías renovables tiene un costo desde 3USD a 8USD por kilogramo, mientras
que producirlo a partir de gas natural cuesta entre 0,5 USD y 1,7 USD por kilogramo. En el mismo estudio se estima que aquellas zonas que
tengan generación de energía mayoritariamente renovable verán una disminución del costo más pronto, alcanzando en 2030 entre 1,3 y
3,5 USD por kg.</p>
<p>En cuanto al consumo del hidrógeno, un camión de clase 8 (peso bruto inferior 15.000 kilogramos y transporte de cargas muy pesadas a
larga distancia) es entre 8,8 y 14,8 km por kg de hidrógeno.</p>
        `), new SubtitleEntryImpl('Referencias', `
<ul class="references-list">
<li>Cunanan, C., Tran, M.-K., Lee, Y., Kwok, S., Leung, V., Fowler, M., 2021. A Review of Heavy-Duty Vehicle Powertrain Technologies: Diesel
Engine Vehicles, Battery Electric Vehicles, and Hydrogen Fuel Cell Electric Vehicles. Clean Technol. 3, 474–489.
https://doi.org/10.3390/cleantechnol3020028</li>
<li>Global Hydrogen Review 2021, 2021. . Glob. Hydrog. Rev. 2021. https://doi.org/10.1787/39351842-en</li>
<li>Hydrogen stations: FAQ - H2.LIVE [WWW Document], n.d. URL https://h2.live/en/faq/ (accessed 12.9.21).</li>
<li>NACFE, 2020. Making Sense of Heavy-Duty Hydrogen Fuel.</li>
</ul>
        `)]
      },
      {
        title: '¿Cómo mejoro la operativa diaria?',
        subtitles: [new SubtitleEntryImpl('Seguridad', `
<p>Cualquier combustible que se utilice para el transporte tiene que ser seguro, porque así lo determina el mercado que utiliza la
tecnología. Es por este motivo que tanto los fabricantes de camiones como las empresas distribuidoras de hidrógeno ponen especial foco en la
seguridad.</p>
<p>Como se comentó anteriormente, el hidrógeno se ha utilizado de forma segura durante muchas décadas y como con cualquier otro combustible,
existen estándares internacionales para el manejo del combustible y para la mitigación de riesgos asociados (CHBC, 2018). EL hidrógeno es
tan seguro como cualquier otro combustible y los tanques son sometidos a pruebas rigurosas que incluyen pruebas de choque, disparos y
requisitos de rendimientos. Además, los materiales utilizados para los tanques son de alta resistencia y son sometidos a más del doble de la
presión máxima de servicio.</p>
<p>Por otro lado, las estaciones de carga de combustible de hidrógeno que ya existen tienen sistemas de protección redundantes por lo que es
inviable sobre presurizar el tanque en el momento de hacer la recarga.</p>
<p>En cuanto al hidrógeno como combustible, éste presenta ciertas ventajas frente a otros combustibles fósiles. Entre ellos se encuentra que
no es un gas tóxico, se disipa rápidamente a la atmósfera en caso de que el tanque tenga un fallo o una pérdida e irradia menos calor cuando
se quema. Vinculado al último punto, como cualquier otro combustible, el hidrógeno debe entrar en contacto con un elemento oxidante (como el
aire) para reaccionar y liberar su energía química almacenada. En los tanques de hidrógeno, a diferencia a los tanques de los demás
combustibles fósiles, no hay presencia de ningún agente oxidante. De esta forma, si por algún motivo se tiene una perforación del tanque, el
hidrógeno escapa y esta liberación evita que entre oxígeno y que pueda producir una explosión en el tanque. Además, el hidrógeno es 14 veces
menos denso que el aire por lo que tiende a subir rápidamente en la atmósfera, provocando que la explosión ocurra por encima del camión y no
por debajo, como ocurre con los combustibles fósiles cuando hay pérdidas.</p>
        `), new SubtitleEntryImpl('Capacitación de personal', `
<p>El entrenamiento tanto de los conductores como de los técnicos siempre se recomienda cuando se adoptan nuevas tecnologías. El
entrenamiento debería incluir conocimientos del combustible, de las condiciones de operación, de los sistemas de seguridad, del uso de las
estaciones de servicio, entre otras.</p>
        `), new SubtitleEntryImpl('Cuándo considerar una flota de camiones a hidrógeno', `
<p>Ambas tecnologías de camiones eléctricos (a batería y a hidrógeno) son complementarias. Mientras que los camiones con batería eléctrica
son ideales para operaciones con 150-250 km diarios, con retorno al centro de distribución para recargar la batería, la tecnología de celda
de combustible de hidrógeno presenta la ventaja de que tiene un tiempo de llenado de tanque muy bajo en comparación a las horas de recarga
de batería y una autonomía mayor, que hace posibles operaciones con muchos km diarios y no necesariamente con retorno a la base. Además,
como se mencionó en apartados anteriores, las celdas de hidrógeno permiten transportar mayores cargas.</p>
<p>Los puntos a tener en cuenta para considerar el uso de camiones fueron resumidos en el reporte realizado en 2020 por NACFE:</p>
<div class="q-twelve-sq-three-img-container"><img src="assets/img/pyr-p12-i1.png" alt="poster NACFE"></div>
        `), new SubtitleEntryImpl('Referencias', `
<ul class="references-list">
<li>CHBC, 2018. CHBC Hydrogen FAQ 1–16.</li>
<li>NACFE, 2020. Making Sense of Heavy-Duty Hydrogen Fuel.</li>
</ul>
        `)]
      }
    ]
  ];

  faqEntrySet: FaqEntry[] = [
    {
      title: 'Preguntas Generales',
      questionSet: this.questionSets[0],
      styles: {
        backgroundColor: '#073763',
        opacity: 0.76
      },
      classes: {
        counterClass: 'set-1'
      }
    },
    {
      title: 'Preguntas referidas a camiones a batería',
      questionSet: this.questionSets[1],
      styles: {
        backgroundColor: '#A6C34E',
        opacity: 0.41
      },
      classes: {
        counterClass: 'set-2'
      }
    },
    {
      title: 'Preguntas referidas a camiones a hidrógeno',
      questionSet: this.questionSets[2],
      styles: {
        backgroundColor: '#2D8BBA',
        opacity: 0.48
      },
      classes: {
        counterClass: 'set-3'
      }
    }
  ];

  constructor(private router: Router, private elementRef: ElementRef) {
  }

  ngAfterViewInit(): void {
    this.bindScrollToAnchorTag();
  }

  // convertContentToSafeHtml(): void {
  //   this.questionSets.map<QuestionEntry[]>(qustionEntries => qustionEntries.map<QuestionEntry>(questionEntry => {
  //
  //     const subtitles = questionEntry.subtitles.map<SubtitleEntryImpl>(subtitleEntryImpl => {
  //
  //       if (typeof subtitleEntryImpl.subtitle !== 'string') {
  //         return subtitleEntryImpl;
  //       }
  //
  //       return {...subtitleEntryImpl, subtitle: this.sanitizer.bypassSecurityTrustHtml(subtitleEntryImpl.subtitle)};
  //     });
  //
  //     return {...questionEntry, subtitles};
  //   }));
  // }

  toggleSubtitlesListVisibility(currentTarget: EventTarget | null): void {

    if (!currentTarget) {
      return;
    }

    const header = currentTarget as HTMLElement;
    const subList = header.parentElement?.querySelector('.subtitles-list');

    if (!subList) {
      return;
    }

    subList.classList.toggle('hide-element');
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (!(event.target instanceof HTMLAnchorElement)) {
      return;
    }

    event.preventDefault();
    const target = event.target as HTMLAnchorElement;
    this.router.navigateByUrl(target.pathname);
  }

  private bindScrollToAnchorTag(): void {
    // console.log(this.elementRef.nativeElement.shadowRoot?.querySelector('.content-container'));
    // console.log(this.elementRef.nativeElement.shadowRoot.querySelector('[data-truck-models-anchor-tag]'));
    // console.log(this.elementRef.nativeElement.shadowRoot.querySelector('.title-container'));
    // const shadowRoot = this.elementRef.nativeElement.shadowRoot;
    //
    // if (!shadowRoot) {
    //   return;
    // }
    //
    // const anchorTag = shadowRoot.querySelector('[data-truck-models-anchor-tag]') as HTMLElement;
    const anchorTag = this.elementRef.nativeElement.shadowRoot.querySelector('[data-truck-models-anchor-tag]') as HTMLElement;

    if (!anchorTag) {
      return;
    }

    anchorTag.addEventListener(
      'click', () => {
        this.scrollToTruckModels();
      });
  }

  // getScrollingElement(): string {
  //   return `<a class="black-underline" onclick="${this.scrollToTruckModels()}">Oferta de camiones en Uruguay</a>`;
  // }

  scrollToTruckModels(): void {

    const target = this.elementRef.nativeElement.shadowRoot.querySelector('[data-truck-models-in-uruguay]') as HTMLElement;

    if (!target) {
      return;
    }

    target.parentElement?.parentElement?.parentElement?.classList.remove('hide-element');

    target.parentElement?.classList.add('show-text');
    target.parentElement?.classList.remove('hide-text');

    target.scrollIntoView({behavior: 'smooth', block: 'center'});
  }
}
